import Layout from "../layouts/Layout";
import CompanyUserLayout from "../layouts/CompanyUserLayout";
import CompanyAdminLayout from "../layouts/CompanyAdminLayout";
import AdminLayout from "../layouts/AdminLayout";
import TopologyCanvas from "../components/topology/TopologyCanvas";
import NodeContextMenu from "../components/topology/NodeContextMenu";
import TopologyActionsPanel from "../components/topology/TopologyActionsPanel";
import PropertiesModal from "../components/topology/PropertiesModal";
import JsonPreviewModal from "../components/topology/JsonPreviewModal";
import PlanViewModal from "../components/topology/PlanViewModal";
import ConfirmModal from "../components/topology/ConfirmModal";
import TextInputModal from "../components/common/TextInputModal";
import TopologyPreviewModal from "../components/topology/TopologyPreviewModal";
import ImportOptionsModal from "../components/topology/ImportOptionsModal";
import DeviceNode from "../components/topology/DeviceNode";
import AIChat from "../components/topology/AIChat";
import ChatHistorySidebar from "../components/topology/ChatHistorySidebar";
import DevicesPaletteContent from "../components/topology/DevicesPaletteContent";
import WalkthroughMenu from "../components/topology/walkthrough/WalkthroughMenu";
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { applyNodeChanges, applyEdgeChanges, addEdge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  getNodeTypes,
  customDeviceIcon,
  getCategoryFallbackIcon,
  initialNodes,
  initialEdges,
} from "../data/topology.config";
import { DeviceTypesContext } from "../context/DeviceTypesContext";
import { applyDagreLayout } from "../utils/layoutUtils";
import { applyDefaultMetadata } from "../utils/metadataDefaults";
import {
  listThreads,
  createThread,
  deleteThread,
  getThreadState,
  getThreadMessages,
  getThreadCanvas,
  syncThreadCanvas,
  sendChatMessage,
  resumeChatMessage,
  submitTopology,
  getSavedTopologies,
  getDefaultTopologies,
  getDeviceTypes,
  getDeviceCategories,
  uploadTopologyImage,
  importTopologyJson,
  sendChatMessageStream,
  stopChatGeneration,
  getCanvasAtCheckpoint,
  getPlanList,
  getPlanVersion,
} from "../api/services/topologyService";
import { useTranslation } from "react-i18next";
import { useToast } from "../components/ToastContext";
import FullScreenLoader from "../components/FullScreenLoader";
import { useAuth } from "../context/AuthContext";
import {
  createMessage,
  fromApiMessage,
  startsWithFullyCapitalizedWord,
} from "../utils/messageUtils";
import "../assets/styles/colors.css";

/** listThreads() response threads → session shape for sidebar. */
function threadsToSessions(threads) {
  const list = Array.isArray(threads) ? threads : [];
  return list.map((thread) => ({
    id: thread.thread_id,
    title: thread.last_message_preview || `Thread ${thread.thread_id}`,
    updated_at: thread.last_updated,
    created_at: thread.last_updated,
    message_count: thread.message_count,
  }));
}

/**
 * Tag plan messages with planOptions. Only the LAST plan message per turn
 * (between two user messages) is tagged. The most recent plan overall gets
 * active buttons if it is still pending approval; all others get "view".
 *
 * @param {Array} messages - message array (mutated in place)
 * @param {{ has_interrupt?: boolean, plan_status?: string, mode?: string }} stateInfo
 */
function tagPlanMessages(messages, stateInfo = {}) {
  const lastPlanPerTurn = new Set();
  let lastPlanIdx = null;
  for (let i = 0; i < messages.length; i++) {
    const pm = messages[i];
    if (pm?.role === "user") {
      if (lastPlanIdx !== null) lastPlanPerTurn.add(lastPlanIdx);
      lastPlanIdx = null;
    } else if (
      pm?.role === "assistant" &&
      typeof pm.content === "string" &&
      /plan (generated|revised)/i.test(pm.content)
    ) {
      lastPlanIdx = i;
    }
  }
  if (lastPlanIdx !== null) lastPlanPerTurn.add(lastPlanIdx);

  const lastPlanOverall = messages
    .map((m, j) =>
      m?.role === "assistant" &&
      /plan (generated|revised)/i.test(m.content || "")
        ? j
        : -1,
    )
    .filter((j) => j >= 0)
    .pop();

  for (const idx of lastPlanPerTurn) {
    const pm = messages[idx];
    // Skip messages that already have planOptions set (e.g. newly created from interrupt_data)
    if (pm.planOptions && pm.planOptions.length > 0) continue;
    const isActivePending =
      stateInfo.has_interrupt &&
      stateInfo.plan_status === "pending_approval" &&
      stateInfo.mode === "plan" &&
      idx === lastPlanOverall;

    if (isActivePending) {
      pm.planOptions = ["approve", "revise", "cancel"];
      pm.planStatus = "pending_approval";
    } else {
      pm.planOptions = ["view"];
      pm.planStatus = "built";
    }
  }
}
/** getSavedTopologies() response → array of saved topology items. */
function normalizeSavedTopologiesList(savedRes) {
  if (Array.isArray(savedRes?.saved_topologies))
    return savedRes.saved_topologies;
  if (Array.isArray(savedRes?.topologies)) return savedRes.topologies;
  if (Array.isArray(savedRes)) return savedRes;
  return [];
}

/** getDefaultTopologies() response → array of default topology items. */
function normalizeDefaultTopologiesList(defaultRes) {
  if (Array.isArray(defaultRes?.default_topologies))
    return defaultRes.default_topologies;
  if (Array.isArray(defaultRes?.topologies)) return defaultRes.topologies;
  if (Array.isArray(defaultRes)) return defaultRes;
  return [];
}

function getIsDarkMode() {
  if (typeof window === "undefined") return false;
  return (
    document.documentElement.classList.contains("dark") ||
    window.matchMedia("(prefers-color-scheme: dark)").matches
  );
}

function getEdgeColor() {
  return getIsDarkMode() ? "var(--color-primary)" : "#16a34a";
}

function deepCloneTopology(value) {
  if (value == null) return value;
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value));
}

/** Canonical topology → React Flow nodes/edges. */
function topologyToReactFlow(topology, existingNodes = [], options = {}) {
  if (!topology) return { nodes: [], edges: [] };

  const {
    applyLayout = true,
    layoutDirection = "TB",
    typeToCategory = null,
  } = options;

  const existingPositions = new Map();
  existingNodes.forEach((node) => {
    if (node && node.id && node.position) {
      const nodeId = String(node.id);
      const pos = node.position;
      if (typeof pos.x === "number" && typeof pos.y === "number") {
        existingPositions.set(nodeId, pos);
      }
    }
  });

  const topologyNodes = Array.isArray(topology.nodes) ? topology.nodes : [];
  let nodes = topologyNodes
    .map((n, idx) => {
      if (!n || !n.id) return null;

      const nodeId = String(n.id);
      const existingPos = existingPositions.get(nodeId);

      const position =
        existingPos &&
        typeof existingPos.x === "number" &&
        typeof existingPos.y === "number"
          ? existingPos
          : {
              x: (idx % 4) * 220,
              y: Math.floor(idx / 4) * 160,
            };

      const canonicalData =
        n.data && typeof n.data === "object" ? { ...n.data } : {};
      const nodeType =
        canonicalData.nodeType || n.type || canonicalData.type || "node";
      const label =
        canonicalData.label ||
        n.label ||
        `${nodeType || "node"}: ${n.id ?? nodeId}`;
      const category =
        typeToCategory?.[nodeType] ?? canonicalData.category ?? undefined;

      return {
        id: nodeId,
        position,
        data: {
          ...canonicalData,
          nodeType,
          label,
          ...(category != null && { category }),
        },
        type: "device",
        connectable: true,
        draggable: true,
      };
    })
    .filter(Boolean);

  const nodeIds = new Set(nodes.map((n) => String(n.id)));
  const edges = (topology.edges || topology.connections || [])
    .map((c) => {
      const source = c.from || c.source;
      const target = c.to || c.target;
      const edgeData =
        c.data && typeof c.data === "object" ? { ...c.data } : undefined;
      const hasAnyData = edgeData && Object.keys(edgeData).length > 0;

      return {
        source: source ? String(source) : null,
        target: target ? String(target) : null,
        data: edgeData,
        label: c.label || (hasAnyData ? "⬤" : ""),
      };
    })
    .filter(
      (c) =>
        c.source && c.target && nodeIds.has(c.source) && nodeIds.has(c.target),
    )
    .map((c, idx) => {
      const color = getEdgeColor();
      return {
        id: `e-${c.source}-${c.target}-${idx}`,
        source: String(c.source),
        target: String(c.target),
        label: c.label || "",
        type: "smoothstep",
        style: { stroke: color, strokeWidth: 2 },
        ...(c.label
          ? {
              label: "⬤",
              labelStyle: {
                fill: color,
                fontWeight: 900,
                fontSize: 14,
                cursor: "pointer",
              },
              labelBgStyle: {
                fill: color,
                fillOpacity: 1,
                stroke: color,
                strokeWidth: 2,
              },
              labelBgPadding: [5, 5],
              labelBgBorderRadius: 50,
              labelShowBg: true,
            }
          : {}),
        animated: false,
        deletable: true,
        data: c.data,
      };
    });

  const hasExistingPositions = nodes.some((n) => existingPositions.has(n.id));
  if (applyLayout && !hasExistingPositions && nodes.length > 0) {
    nodes = applyDagreLayout(nodes, edges, {
      direction: layoutDirection,
    });
  }

  return { nodes, edges };
}

/**
 * Convert API canvas to React Flow nodes/edges with dagre auto-layout.
 * typeToCategory: optional map of device type → category for layout tiers.
 */
function applyCanvasFromApi(canvasData, options = {}) {
  const topology = {
    nodes: Array.isArray(canvasData?.nodes) ? canvasData.nodes : [],
    edges: Array.isArray(canvasData?.edges) ? canvasData.edges : [],
  };
  return topologyToReactFlow(topology, [], { applyLayout: true, ...options });
}

function extractTopologyMeta(topologyData) {
  if (!topologyData || typeof topologyData !== "object") return null;

  const resolvedTitle =
    topologyData.title || topologyData.name || topologyData.ai_title;
  const resolvedDescription =
    topologyData.description || topologyData.ai_description || "";

  return {
    title: resolvedTitle || "Untitled Topology",
    name: topologyData.name || resolvedTitle || "Untitled Topology",
    version: topologyData.version || "1.0",
    description: resolvedDescription,
    metadata:
      topologyData.metadata && typeof topologyData.metadata === "object"
        ? topologyData.metadata
        : {},
    ai_title: topologyData.ai_title || null,
    ai_description: topologyData.ai_description || null,
  };
}

/** React Flow nodes/edges → canonical topology. */
function reactFlowToTopology(rfNodes, rfEdges, baseTopology = null) {
  const topology = baseTopology || {
    name: "Untitled Topology",
    version: "1.0",
    description: "",
    nodes: [],
    edges: [],
    metadata: {
      created_at: new Date().toISOString(),
      created_by: "user",
      environment: "mvp",
    },
  };

  const nodeMap = new Map();
  if (baseTopology?.nodes) {
    baseTopology.nodes.forEach((n) => nodeMap.set(n.id, { ...n }));
  }

  topology.nodes = rfNodes.map((rfNode) => {
    const existing = nodeMap.get(rfNode.id);
    const data = rfNode.data || {};
    const { label, nodeType, ...metadata } = data;

    const typeFromLabel =
      typeof label === "string" && label.includes(":")
        ? label.split(":")[0].trim()
        : undefined;

    const type =
      nodeType ||
      existing?.type ||
      typeFromLabel ||
      existing?.nodeType ||
      "node";

    const finalLabel =
      label || existing?.label || `${type}: ${String(rfNode.id)}`;

    const node = {
      id: rfNode.id,
      ...(existing || {}),
      type,
      label: finalLabel,
    };

    const pos = rfNode.position || existing?.position;
    if (pos && typeof pos.x === "number" && typeof pos.y === "number") {
      node.position = {
        x: Number(pos.x),
        y: Number(pos.y),
      };
    }

    // const hasMeta = Object.keys(metadata).length > 0;
    // if (hasMeta) {
    //   node.data = metadata;
    // } else if (existing?.data) {
    //   node.data = existing.data;
    // }
    const baseData =
      Object.keys(metadata).length > 0 ? metadata : existing?.data || {};
    node.data = { ...baseData, nodeType: type };

    return node;
  });

  const baseEdges = baseTopology?.edges || [];

  topology.edges = rfEdges.map((rfEdge) => {
    const original = baseEdges.find(
      (e) =>
        (e.from === rfEdge.source && e.to === rfEdge.target) ||
        (e.source === rfEdge.source && e.target === rfEdge.target),
    );
    const edgeData = rfEdge.data || {};
    const { label, ...edgeMeta } = edgeData;

    const edge = {
      from: rfEdge.source,
      to: rfEdge.target,
      type: "ethernet",
      id: rfEdge.id || original?.id || `e-${rfEdge.source}-${rfEdge.target}`,
      source: rfEdge.source,
      target: rfEdge.target,
    };

    if (rfEdge.label || label) {
      edge.label = rfEdge.label || label;
    }

    const hasEdgeMeta = Object.keys(edgeMeta).length > 0;
    if (hasEdgeMeta) {
      edge.data = edgeMeta;
    } else if (original?.data) {
      edge.data = original.data;
    }

    return edge;
  });

  return topology;
}

/** Validate/normalize nodes for React Flow. */
function validateNodes(nodes) {
  if (!Array.isArray(nodes)) return [];

  return nodes
    .filter((node) => node && node.id)
    .map((node, idx) => {
      const hasValidPosition =
        node.position &&
        typeof node.position.x === "number" &&
        typeof node.position.y === "number";

      const nodeData = node.data || {};
      const nodeType =
        nodeData.nodeType || node.nodeType || node.type || "node";
      const label = nodeData.label || node.label || `${nodeType}: ${node.id}`;

      return {
        ...node,
        id: String(node.id),
        type: "device",
        data: {
          ...nodeData,
          nodeType: nodeType,
          label: label,
        },
        position: hasValidPosition
          ? { x: node.position.x, y: node.position.y }
          : {
              x: (idx % 4) * 220,
              y: Math.floor(idx / 4) * 160,
            },
      };
    });
}

function Topology() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { showToast } = useToast();
  const { user } = useAuth();
  // --- Canvas state ---
  const [nodes, setNodes] = useState(initialNodes);
  const [edges, setEdges] = useState(initialEdges);
  const [currentTopology, setCurrentTopology] = useState(null);
  const [hasUnsyncedChanges, setHasUnsyncedChanges] = useState(false);
  const [canonicalCanvas, setCanonicalCanvas] = useState(null);
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null);
  const fileInputRef = useRef(null);
  const reactFlowNodeTypes = useMemo(
    () => ({
      device: DeviceNode,
    }),
    [],
  );

  // --- Device types ---

  const [availableNodeTypes, setAvailableNodeTypes] = useState([]);
  const [deviceTypesLoading, setDeviceTypesLoading] = useState(true);
  const [deviceCategories, setDeviceCategories] = useState([]);
  const typeToCategory = useMemo(
    () =>
      Object.fromEntries(
        availableNodeTypes
          .filter((nt) => nt.category != null)
          .map((nt) => [nt.type, nt.category]),
      ),
    [availableNodeTypes],
  );

  // --- Chat / session ---
  const [chatMessage, setChatMessage] = useState("");
  const [chatHistory, setChatHistory] = useState([]);
  const [aiSessions, setAiSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(() => {
    return localStorage.getItem("topology_selected_session_id") || null;
  });
  const [sessionLoading, setSessionLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [streamingStatus, setStreamingStatus] = useState(null); // Current streaming status message
  const [streamingStatusHistory, setStreamingStatusHistory] = useState([]); // History of status updates
  const [iterationSnapshots, setIterationSnapshots] = useState([]); // Per-iteration validation results
  const [chatMode, setChatMode] = useState("agent");
  // Refs for tracking streaming state
  const streamingAnswerRef = useRef("");
  const streamingMessageIndexRef = useRef(null);
  const abortControllerRef = useRef(null); // holds the active fetch AbortController
  const stoppedByUserRef = useRef(false); // true when stop was user-initiated

  const [chatHistorySidebarOpen, setChatHistorySidebarOpen] = useState(false);
  const [initialThreadsLoading, setInitialThreadsLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [planActionState, setPlanActionState] = useState({
    loading: false,
    option: null,
  });

  // --- Layout / UI ---
  const [layoutDirection, setLayoutDirection] = useState("TB");
  const [mobileChatOpen, setMobileChatOpen] = useState(false);
  const [mobileDevicesOpen, setMobileDevicesOpen] = useState(false);

  // --- Modals & UI ---
  const [contextMenu, setContextMenu] = useState(null);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");
  const [deleteConfirmState, setDeleteConfirmState] = useState({
    show: false,
    sessionId: null,
    loading: false,
  });
  const [defaultSwitchConfirmState, setDefaultSwitchConfirmState] = useState({
    show: false,
    item: null,
  });
  const [nodePropertiesState, setNodePropertiesState] = useState({
    show: false,
    node: null,
  });
  const [edgePropertiesState, setEdgePropertiesState] = useState({
    show: false,
    edge: null,
  });
  const [jsonPreviewOpen, setJsonPreviewOpen] = useState(false);
  const [reviseModalOpen, setReviseModalOpen] = useState(false);
  const [reviseFeedback, setReviseFeedback] = useState("");
  const [submitNameModalOpen, setSubmitNameModalOpen] = useState(false);
  const [submitTopologyName, setSubmitTopologyName] = useState("");
  const [submitState, setSubmitState] = useState({
    loading: false,
    error: null,
    success: false,
  });
  const [submitViolations, setSubmitViolations] = useState([]);
  const [
    submitViolationsPopoverDismissed,
    setSubmitViolationsPopoverDismissed,
  ] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewModalNodes, setPreviewModalNodes] = useState([]);
  const [previewModalEdges, setPreviewModalEdges] = useState([]);
  const [previewModalTitle, setPreviewModalTitle] = useState("");
  const [previewModalInfo, setPreviewModalInfo] = useState({
    aiTitle: null,
    aiDescription: null,
    nodeCount: null,
    edgeCount: null,
  });
  const [savedTopologies, setSavedTopologies] = useState([]);
  const [defaultTopologies, setDefaultTopologies] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [jsonUploading, setJsonUploading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // undefined = no maxHeight applied (mobile stacked layout)
  const [sidebarMaxHeight, setSidebarMaxHeight] = useState(undefined);

  // --- Revert Preview State ---
  const [isRevertPreview, setIsRevertPreview] = useState(false);
  const [revertSourceIndex, setRevertSourceIndex] = useState(null);
  const [preRevertCanvas, setPreRevertCanvas] = useState(null);

  // --- Plan View State ---
  const [planViewOpen, setPlanViewOpen] = useState(false);
  const [planViewMd, setPlanViewMd] = useState(null);
  const [planViewStatus, setPlanViewStatus] = useState(null);
  const [planViewLoading, setPlanViewLoading] = useState(false);
  const [planVersions, setPlanVersions] = useState([]);
  const [planCurrentVersion, setPlanCurrentVersion] = useState(0);
  const [planTotalVersions, setPlanTotalVersions] = useState(0);
  const [planVersionLoading, setPlanVersionLoading] = useState(false);

  // --- Effects ---
  useEffect(() => {
    // OVERHEAD = navbar + page padding + page header + toolbar + gaps + footer
    // Approximate total of fixed surrounding elements (~510px)
    const OVERHEAD = 510;
    const BASELINE_H = 600;
    // lg breakpoint — below this the columns stack and maxHeight must not apply
    const LG_BREAKPOINT = 1024;
    const checkSize = () => {
      if (window.innerWidth < LG_BREAKPOINT) {
        // Mobile: columns are stacked, never constrain grid height
        setSidebarMaxHeight(undefined);
        return;
      }
      // Desktop: innerHeight grows both on large screens and when zoomed out,
      const available = window.innerHeight - OVERHEAD;
      setSidebarMaxHeight(Math.min(Math.max(BASELINE_H, available), 1600));
    };
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setDeviceTypesLoading(true);
      try {
        const [typesRes, categoriesRes] = await Promise.all([
          getDeviceTypes(),
          getDeviceCategories(),
        ]);
        const raw = Array.isArray(typesRes?.device_types)
          ? typesRes.device_types
          : Array.isArray(typesRes?.data)
            ? typesRes.data
            : Array.isArray(typesRes)
              ? typesRes
              : [];
        const apiBase = (import.meta.env.VITE_API_BASE_URL || "").replace(
          /\/$/,
          "",
        );
        const resolveIcon = (value) => {
          if (value == null || value === "") return value;
          const s = String(value).trim();
          if (
            s.startsWith("http://") ||
            s.startsWith("https://") ||
            s.startsWith("data:")
          )
            return s;
          if (s.startsWith("/assets/icons/svg/")) return s;
          return apiBase ? `${apiBase}/${s.replace(/^\//, "")}` : s;
        };
        const normalizeDeviceLabel = (value) => {
          if (value == null) return value;
          const s = String(value).trim();
          if (!s) return s;
          if (s.startsWith("**")) {
            const withoutPrefix = s.replace(/^\*\*\s*/, "");
            const withoutSuffix = withoutPrefix.replace(/\s*\*\*$/, "");
            return withoutSuffix || withoutPrefix;
          }
          return s;
        };
        const normalized = raw.map((item) => {
          const type = item.type ?? item.id ?? String(item);
          const labelRaw =
            item.default_label ?? item.label ?? item.name ?? type;
          const label = normalizeDeviceLabel(labelRaw);
          const rawIcon =
            item.icon_path ?? item.icon ?? item.icon_url ?? item.image_url;
          const resolved = resolveIcon(rawIcon);
          const category = item.category ?? "Other / Special Purpose";
          const icon = resolved || getCategoryFallbackIcon(category);
          const metadata =
            item.metadata_template != null &&
            typeof item.metadata_template === "object"
              ? item.metadata_template
              : item.metadata != null && typeof item.metadata === "object"
                ? item.metadata
                : item.default_metadata != null &&
                    typeof item.default_metadata === "object"
                  ? item.default_metadata
                  : {};
          return { type, label, icon, category, metadata };
        });
        if (!cancelled) setAvailableNodeTypes(normalized);
        const catList = Array.isArray(categoriesRes)
          ? categoriesRes
          : Array.isArray(categoriesRes?.categories)
            ? categoriesRes.categories
            : Array.isArray(categoriesRes?.data)
              ? categoriesRes.data
              : [];
        if (!cancelled) setDeviceCategories(catList);
      } catch (err) {
        console.error(
          "Failed to load device types from API, using fallback:",
          err,
        );
        if (!cancelled) setAvailableNodeTypes(getNodeTypes(t));
        if (!cancelled) setDeviceCategories([]);
      } finally {
        if (!cancelled) setDeviceTypesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    const fetchAiData = async () => {
      try {
        setInitialThreadsLoading(true);
        try {
          const [savedRes, defaultRes] = await Promise.all([
            getSavedTopologies(),
            getDefaultTopologies(false),
          ]);
          setSavedTopologies(normalizeSavedTopologiesList(savedRes));
          setDefaultTopologies(normalizeDefaultTopologiesList(defaultRes));
        } catch (_) {
          setSavedTopologies([]);
          setDefaultTopologies([]);
        }
        const response = await listThreads();
        const threads = Array.isArray(response?.threads)
          ? response.threads
          : [];
        setAiSessions(threadsToSessions(threads));
        // Auto-restore last active session on page refresh
        const storedSessionId = localStorage.getItem(
          "topology_selected_session_id",
        );
        if (
          storedSessionId &&
          threads.some((t) => t.thread_id === storedSessionId)
        ) {
          // We call handleSelectSession but it's not stable yet here, so use the raw logic
          // Instead, set a flag and let a separate effect pick it up
          setSelectedSessionId(storedSessionId);
        }
      } catch (err) {
        console.error("Error fetching AI topology data:", err);
      } finally {
        setInitialThreadsLoading(false);
      }
    };
    fetchAiData();
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setContextMenu(null);
    };
    if (contextMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => {
        document.removeEventListener("click", handleClickOutside);
      };
    }
  }, [contextMenu]);

  useEffect(() => {
    if (selectedSessionId) {
      localStorage.setItem("topology_selected_session_id", selectedSessionId);
    } else {
      localStorage.removeItem("topology_selected_session_id");
    }
  }, [selectedSessionId]);

  useEffect(() => {
    // Clear the stored session when user navigates away from this page
    return () => {
      localStorage.removeItem("topology_selected_session_id");
    };
  }, []);

  // --- Canvas callbacks ---
  const onNodesChange = useCallback((changes) => {
    setNodes((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot));
    setHasUnsyncedChanges(true);
  }, []);
  const onEdgesChange = useCallback((changes) => {
    setEdges((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot));
    setHasUnsyncedChanges(true);
  }, []);
  const onConnect = useCallback((params) => {
    const styledParams = {
      ...params,
      type: "smoothstep",
      style: { stroke: "var(--color-primary)", strokeWidth: 2 },
      animated: false,
      deletable: true,
    };
    setEdges((edgesSnapshot) => addEdge(styledParams, edgesSnapshot));
    setHasUnsyncedChanges(true);
  }, []);

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData("application/reactflow", nodeType);
    event.dataTransfer.effectAllowed = "move";
  };

  const handleAddDevice = useCallback(
    (nodeType) => {
      const nodeTypeConfig = availableNodeTypes.find(
        (nt) => nt.type === nodeType,
      );
      const nodeLabel = nodeTypeConfig?.label || nodeType;

      const position = reactFlowInstance.current
        ? reactFlowInstance.current.screenToFlowPosition({
            x: window.innerWidth / 2,
            y: window.innerHeight / 2,
          })
        : { x: 200 + Math.random() * 100, y: 200 + Math.random() * 100 };

      const newNode = {
        id: `${nodeType}-${Date.now()}`,
        type: "device",
        position,
        data: {
          label: nodeLabel,
          nodeType: nodeType,
          category: nodeTypeConfig?.category ?? undefined,
        },
      };

      const apiMetadata = nodeTypeConfig?.metadata ?? {};
      applyDefaultMetadata(newNode, nodeType, nodeLabel, apiMetadata);

      setNodes((nds) => nds.concat(newNode));
      setHasUnsyncedChanges(true);
      setMobileDevicesOpen(false);
      showToast(`${nodeLabel} added to canvas`, "success");
    },
    [availableNodeTypes, showToast],
  );

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const nodeType = event.dataTransfer.getData("application/reactflow");
      if (!nodeType || !reactFlowWrapper.current) {
        return;
      }

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const position = reactFlowInstance.current
        ? reactFlowInstance.current.screenToFlowPosition({
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          })
        : {
            x: event.clientX - reactFlowBounds.left,
            y: event.clientY - reactFlowBounds.top,
          };

      const nodeTypeConfig = availableNodeTypes.find(
        (nt) => nt.type === nodeType,
      );
      const nodeLabel = nodeTypeConfig?.label || nodeType;
      const newNode = {
        id: `${nodeType}-${Date.now()}`,
        type: "device",
        position,
        data: {
          label: nodeLabel,
          nodeType: nodeType,
          category: nodeTypeConfig?.category ?? undefined,
        },
      };

      const apiMetadata = nodeTypeConfig?.metadata ?? {};
      applyDefaultMetadata(newNode, nodeType, nodeLabel, apiMetadata);

      setNodes((nds) => nds.concat(newNode));
      setHasUnsyncedChanges(true);
    },
    [availableNodeTypes, setNodes],
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }, []);

  // --- Context menu & node edit ---
  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    setContextMenu({
      id: node.id,
      top: event.clientY,
      left: event.clientX,
    });
  }, []);

  const handleDeleteNode = useCallback(() => {
    if (contextMenu?.id) {
      setNodes((nds) => nds.filter((node) => node.id !== contextMenu.id));
      setEdges((eds) =>
        eds.filter(
          (edge) =>
            edge.source !== contextMenu.id && edge.target !== contextMenu.id,
        ),
      );
      setContextMenu(null);
    }
  }, [contextMenu]);

  const handleRenameNode = useCallback(() => {
    if (contextMenu?.id) {
      const node = nodes.find((n) => n.id === contextMenu.id);
      if (node) {
        setEditingNodeId(contextMenu.id);
        setEditingLabel(node.data.label || "");
        setContextMenu(null);
      }
    }
  }, [contextMenu, nodes]);

  const handleEditPropertiesFromContext = useCallback(() => {
    if (contextMenu?.id) {
      const node = nodes.find((n) => n.id === contextMenu.id);
      if (node) {
        setNodePropertiesState({ show: true, node });
        setContextMenu(null);
      }
    }
  }, [contextMenu, nodes]);

  const handleSaveRename = useCallback(() => {
    if (editingNodeId) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === editingNodeId
            ? { ...node, data: { ...node.data, label: editingLabel } }
            : node,
        ),
      );
      setEditingNodeId(null);
      setEditingLabel("");
    }
  }, [editingNodeId, editingLabel]);

  const handleCancelRename = useCallback(() => {
    setEditingNodeId(null);
    setEditingLabel("");
  }, []);

  const handleAutoLayout = useCallback(() => {
    if (!nodes || nodes.length === 0) return;
    const layoutNodes = applyDagreLayout(nodes, edges, { direction: "TB" });
    setNodes(layoutNodes);
  }, [nodes, edges]);

  const handleNodeDoubleClick = useCallback((event, node) => {
    event.preventDefault();
    setNodePropertiesState({ show: true, node });
  }, []);

  const handleSaveNodeProperties = useCallback(
    (newData) => {
      const nodeId = nodePropertiesState.node?.id;
      if (!nodeId) return;
      setNodes((nds) =>
        nds.map((node) =>
          node.id === nodeId ? { ...node, data: newData } : node,
        ),
      );
    },
    [nodePropertiesState.node],
  );

  const handleCloseNodeProperties = useCallback(() => {
    setNodePropertiesState({ show: false, node: null });
  }, []);

  // --- Edge properties ---
  const handleEdgeClick = useCallback((event, edge) => {
    event.preventDefault();
    setEdgePropertiesState({ show: true, edge });
  }, []);

  const handleSaveEdgeProperties = useCallback(
    (newData) => {
      const edgeId = edgePropertiesState.edge?.id;
      if (!edgeId) return;

      const hasAnyData = newData && Object.keys(newData).length > 0;
      const newLabel = hasAnyData ? "⬤" : "";

      setEdges((eds) =>
        eds.map((edge) => {
          if (edge.id !== edgeId) return edge;

          const updated = {
            ...edge,
            type: edge.type || "smoothstep",
            style: edge.style || {
              stroke: "var(--color-primary)",
              strokeWidth: 2,
            },
            label: newLabel,
            data: newData,
          };

          if (newLabel) {
            updated.labelStyle = {
              fill: "var(--color-primary)",
              fontWeight: 900,
              fontSize: 14,
              cursor: "pointer",
            };
            updated.labelBgStyle = {
              fill: "var(--color-primary)",
              fillOpacity: 1,
              stroke: "var(--color-primary)",
              strokeWidth: 2,
            };
            updated.labelBgPadding = [5, 5];
            updated.labelBgBorderRadius = 50;
            updated.labelShowBg = true;
          } else {
            updated.labelStyle = undefined;
            updated.labelBgStyle = undefined;
            updated.labelBgPadding = undefined;
            updated.labelBgBorderRadius = undefined;
            updated.labelShowBg = undefined;
          }

          return updated;
        }),
      );
    },
    [edgePropertiesState.edge],
  );
  const handleCloseEdgeProperties = useCallback(() => {
    setEdgePropertiesState({ show: false, edge: null });
  }, []);

  // --- Toolbar ---
  const handlePreviewJson = useCallback(() => {
    setJsonPreviewOpen(true);
  }, []);

  const handleCloseJsonPreview = useCallback(() => {
    setJsonPreviewOpen(false);
  }, []);

  const handleExportJson = useCallback(() => {
    const data = { nodes, edges };
    const blob = new Blob([JSON.stringify(data, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "topology.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [nodes, edges]);

  const handleImportClick = useCallback(() => {
    setImportModalOpen(true);
  }, []);

  const handleImportModalClose = useCallback(() => {
    if (!imageUploading && !jsonUploading) setImportModalOpen(false);
  }, [imageUploading, jsonUploading]);

  const handleSubmitTopologyClick = useCallback(() => {
    if (nodes.length === 0 && edges.length === 0) {
      showToast(
        t("No topology to submit. Add some devices or connections first."),
        "warning",
      );
      return;
    }
    setSubmitTopologyName("");
    setSubmitNameModalOpen(true);
  }, [nodes.length, edges.length, showToast, t]);

  const handleSubmitNameModalClose = useCallback(() => {
    if (!submitState.loading) {
      setSubmitNameModalOpen(false);
      setSubmitTopologyName("");
      setSubmitViolations([]);
      setSubmitState((prev) => (prev.error ? { ...prev, error: null } : prev));
    }
  }, [submitState.loading]);

  const handleSavedTopologyClick = useCallback(
    (item) => {
      const label =
        item?.ai_title ?? item?.name ?? item?.title ?? t("Topology preview");
      const rawNodes =
        item?.nodes ?? item?.canvas_json?.nodes ?? item?.canvas?.nodes ?? [];
      const rawEdges =
        item?.edges ?? item?.canvas_json?.edges ?? item?.canvas?.edges ?? [];
      if (!Array.isArray(rawNodes) && !Array.isArray(rawEdges)) {
        showToast(t("No topology data (nodes/edges) for this item"), "warning");
        return;
      }
      const canvasNodes = Array.isArray(rawNodes) ? rawNodes : [];
      const canvasEdges = Array.isArray(rawEdges) ? rawEdges : [];
      const getCountOrFallback = (value, fallback) => {
        if (value == null || value === "") return fallback;
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };
      const validatedNodes = validateNodes(canvasNodes);
      const validTypes = [
        "smoothstep",
        "default",
        "straight",
        "step",
        "bezier",
      ];
      const processedEdges = canvasEdges.map((edge) => {
        if (!edge.type || !validTypes.includes(edge.type)) {
          return { ...edge, type: "smoothstep" };
        }
        return edge;
      });
      setPreviewModalNodes(validatedNodes);
      setPreviewModalEdges(processedEdges);
      setPreviewModalTitle(label);
      setPreviewModalInfo({
        aiTitle: item?.ai_title ?? null,
        aiDescription: item?.ai_description ?? null,
        nodeCount: getCountOrFallback(item?.node_count, canvasNodes.length),
        edgeCount: getCountOrFallback(item?.edge_count, canvasEdges.length),
      });
      setPreviewModalOpen(true);
    },
    [t, showToast],
  );

  const loadDefaultTopologyToCanvas = useCallback(
    (item) => {
      const sourceTopology = deepCloneTopology(item);
      const rawNodes =
        sourceTopology?.canvas_json?.nodes ??
        sourceTopology?.canvas?.nodes ??
        sourceTopology?.nodes ??
        [];
      const rawEdges =
        sourceTopology?.canvas_json?.edges ??
        sourceTopology?.canvas?.edges ??
        sourceTopology?.edges ??
        [];

      if (!Array.isArray(rawNodes) && !Array.isArray(rawEdges)) {
        showToast(t("No topology data (nodes/edges) for this item"), "warning");
        return;
      }

      const canvasNodes = Array.isArray(rawNodes) ? rawNodes : [];
      const canvasEdges = Array.isArray(rawEdges) ? rawEdges : [];

      const validatedNodes = validateNodes(canvasNodes);
      const validTypes = [
        "smoothstep",
        "default",
        "straight",
        "step",
        "bezier",
      ];
      const defEdgeColor = getEdgeColor();
      const processedEdges = canvasEdges
        .map((edge, index) => {
          const normalizedSource = edge?.source ?? edge?.from;
          const normalizedTarget = edge?.target ?? edge?.to;
          const edgeData =
            edge?.data && typeof edge.data === "object"
              ? { ...edge.data }
              : undefined;
          const hasAnyData = edgeData && Object.keys(edgeData).length > 0;
          const hasLabel = !!(edge?.label || hasAnyData);

          return {
            ...edge,
            id:
              edge?.id ||
              `e-${String(normalizedSource ?? "unknown")}-${String(normalizedTarget ?? "unknown")}-${index}`,
            source: normalizedSource != null ? String(normalizedSource) : null,
            target: normalizedTarget != null ? String(normalizedTarget) : null,
            type:
              edge?.type && validTypes.includes(edge.type)
                ? edge.type
                : "smoothstep",
            // Keep default-topology edges visually consistent with normal canvas edges.
            style: { stroke: defEdgeColor, strokeWidth: 2 },
            ...(hasLabel
              ? {
                  label: "⬤",
                  labelStyle: {
                    fill: defEdgeColor,
                    fontWeight: 900,
                    fontSize: 14,
                    cursor: "pointer",
                  },
                  labelBgStyle: {
                    fill: defEdgeColor,
                    fillOpacity: 1,
                    stroke: defEdgeColor,
                    strokeWidth: 2,
                  },
                  labelBgPadding: [5, 5],
                  labelBgBorderRadius: 50,
                  labelShowBg: true,
                }
              : {}),
            animated: false,
            deletable: true,
            data: edgeData,
          };
        })
        .filter((edge) => edge.source && edge.target);

      setNodes(validatedNodes);
      setEdges(processedEdges);
      setCanonicalCanvas({ nodes: validatedNodes, edges: processedEdges });
      setCurrentTopology(sourceTopology ?? null);
      setHasUnsyncedChanges(true);
      setPreviewModalOpen(false);

      const loadedName =
        sourceTopology?.name ??
        sourceTopology?.ai_title ??
        sourceTopology?.title ??
        t("Default topology");
      showToast(
        t("Loaded {{name}} into canvas", { name: loadedName }),
        "success",
      );
    },
    [t, showToast],
  );

  const handleDefaultTopologyClick = useCallback(
    (item) => {
      if (aiLoading) {
        showToast(
          t("Please wait until AI finishes building the topology"),
          "warning",
        );
        return;
      }

      const hasCanvasContent = nodes.length > 0 || edges.length > 0;

      if (hasCanvasContent) {
        setDefaultSwitchConfirmState({ show: true, item });
        return;
      }

      loadDefaultTopologyToCanvas(item);
    },
    [
      aiLoading,
      nodes.length,
      edges.length,
      loadDefaultTopologyToCanvas,
      t,
      showToast,
    ],
  );

  const handleDefaultSwitchConfirmClose = useCallback(() => {
    setDefaultSwitchConfirmState({ show: false, item: null });
  }, []);

  const handleDefaultSwitchConfirm = useCallback(() => {
    const pendingItem = defaultSwitchConfirmState.item;
    if (!pendingItem) {
      setDefaultSwitchConfirmState({ show: false, item: null });
      return;
    }

    loadDefaultTopologyToCanvas(pendingItem);
    setDefaultSwitchConfirmState({ show: false, item: null });
  }, [defaultSwitchConfirmState.item, loadDefaultTopologyToCanvas]);

  const handleSubmitTopology = useCallback(
    async (nameFromModal) => {
      const name =
        typeof nameFromModal === "string" ? nameFromModal.trim() : "";

      try {
        setSubmitState({ loading: true, error: null, success: false });
        setSubmitViolations([]);

        let threadId = selectedSessionId;
        if (!threadId) {
          const newThread = await createThread();
          threadId = newThread.thread_id || newThread.id;
          setSelectedSessionId(threadId);
        }

        const canvasTopology = reactFlowToTopology(
          nodes,
          edges,
          currentTopology,
        );
        await syncThreadCanvas(threadId, canvasTopology);
        setCanonicalCanvas({ nodes: [...nodes], edges: [...edges] });
        setHasUnsyncedChanges(false);

        await submitTopology(threadId, name || null);

        setSubmitState({ loading: false, error: null, success: true });
        showToast(t("Topology submitted successfully"), "success");
        setSubmitNameModalOpen(false);
        setSubmitTopologyName("");

        try {
          const savedRes = await getSavedTopologies();
          setSavedTopologies(normalizeSavedTopologiesList(savedRes));
        } catch (_) {}

        setTimeout(() => {
          setSubmitState((prev) => ({ ...prev, success: false }));
        }, 3000);
      } catch (err) {
        console.error("Error submitting topology:", err);
        const message = err?.message || t("Failed to submit topology");

        // Injection rejection: keep modal open, surface structured violations
        // inline so the user can identify which fields to fix before retrying.
        if (err?.code === "TOP_INJ_001") {
          const violations = err?.payload?.detail?.violations || [];
          setSubmitViolations(violations);
          setSubmitState({
            loading: false,
            error: null,
            success: false,
          });
          showToast(
            t("Save blocked: topology contains flagged content"),
            "error",
          );
          return;
        }

        setSubmitState({
          loading: false,
          error: message,
          success: false,
        });
        showToast(message, "error");

        setTimeout(() => {
          setSubmitState((prev) => ({ ...prev, error: null }));
        }, 5000);
      }
    },
    [
      nodes,
      edges,
      currentTopology,
      selectedSessionId,
      t,
      showToast,
      getSavedTopologies,
    ],
  );

  const handleSyncTopology = useCallback(async () => {
    if (!selectedSessionId || (nodes.length === 0 && edges.length === 0)) {
      return;
    }

    try {
      setSyncLoading(true);
      const canvasTopology = reactFlowToTopology(nodes, edges, currentTopology);
      await syncThreadCanvas(selectedSessionId, canvasTopology);
      setCanonicalCanvas({ nodes: [...nodes], edges: [...edges] });
      setHasUnsyncedChanges(false);
      showToast(t("Topology synced with current session"), "success");
    } catch (err) {
      console.error("Failed to sync topology canvas:", err);
      showToast(err?.message || t("Failed to sync topology"), "error");
    } finally {
      setSyncLoading(false);
    }
  }, [selectedSessionId, nodes, edges, currentTopology, t, showToast]);

  // --- Revert Preview Handlers ---
  const handleRevertToMessage = useCallback(
    async (messageIndex, checkpointId) => {
      if (!selectedSessionId || !checkpointId) return;
      if (isRevertPreview && revertSourceIndex === messageIndex) return;

      try {
        // Save current canvas before reverting
        if (!isRevertPreview) {
          setPreRevertCanvas({ nodes: [...nodes], edges: [...edges] });
        }

        const response = await getCanvasAtCheckpoint(selectedSessionId, {
          checkpointId,
        });

        const canvasData = response?.canvas_json || { nodes: [], edges: [] };
        const { nodes: rfNodes, edges: rfEdges } = topologyToReactFlow(
          canvasData,
          nodes,
        );
        setNodes(rfNodes);
        setEdges(rfEdges);

        setIsRevertPreview(true);
        setRevertSourceIndex(messageIndex);
      } catch (err) {
        console.error("Failed to revert to checkpoint:", err);
        showToast(
          err?.message || t("Failed to load canvas at this point"),
          "error",
        );
      }
    },
    [
      selectedSessionId,
      isRevertPreview,
      revertSourceIndex,
      nodes,
      edges,
      showToast,
      t,
    ],
  );

  const handleRevertUndo = useCallback(() => {
    if (preRevertCanvas) {
      setNodes(preRevertCanvas.nodes);
      setEdges(preRevertCanvas.edges);
    }
    setIsRevertPreview(false);
    setRevertSourceIndex(null);
    setPreRevertCanvas(null);
  }, [preRevertCanvas]);

  const handleRevertSave = useCallback(async () => {
    if (!selectedSessionId) return;

    try {
      setSyncLoading(true);
      const canvasTopology = reactFlowToTopology(nodes, edges, currentTopology);
      await syncThreadCanvas(selectedSessionId, canvasTopology);
      setCanonicalCanvas({ nodes: [...nodes], edges: [...edges] });
      setHasUnsyncedChanges(false);
      setIsRevertPreview(false);
      setRevertSourceIndex(null);
      setPreRevertCanvas(null);
      showToast(t("Canvas reverted and saved"), "success");
    } catch (err) {
      console.error("Failed to save reverted canvas:", err);
      showToast(err?.message || t("Failed to save changes"), "error");
    } finally {
      setSyncLoading(false);
    }
  }, [selectedSessionId, nodes, edges, currentTopology, showToast, t]);

  const handleViewPlan = useCallback(async () => {
    if (!selectedSessionId) return;

    setPlanViewOpen(true);
    setPlanViewLoading(true);
    setPlanViewMd(null);
    setPlanViewStatus(null);
    setPlanVersions([]);
    setPlanCurrentVersion(0);
    setPlanTotalVersions(0);

    try {
      const listRes = await getPlanList(selectedSessionId);
      const versions = listRes?.versions || [];
      const total = listRes?.total_versions || 0;
      setPlanVersions(versions);
      setPlanTotalVersions(total);

      if (total > 0) {
        // Load the latest version by default
        const latest = total;
        setPlanCurrentVersion(latest);
        const vRes = await getPlanVersion(selectedSessionId, latest);
        setPlanViewMd(vRes?.plan_md || null);
        setPlanViewStatus(vRes?.plan_status || null);
      }
    } catch (err) {
      console.error("Failed to load plan:", err);
      showToast(err?.message || t("Failed to load plan"), "error");
      setPlanViewOpen(false);
    } finally {
      setPlanViewLoading(false);
    }
  }, [selectedSessionId, showToast, t]);

  const handlePlanVersionChange = useCallback(
    async (version) => {
      if (!selectedSessionId || version < 1 || version > planTotalVersions)
        return;

      setPlanVersionLoading(true);
      try {
        const vRes = await getPlanVersion(selectedSessionId, version);
        setPlanCurrentVersion(version);
        setPlanViewMd(vRes?.plan_md || null);
        setPlanViewStatus(vRes?.plan_status || null);
      } catch (err) {
        console.error("Failed to load plan version:", err);
        showToast(err?.message || t("Failed to load plan version"), "error");
      } finally {
        setPlanVersionLoading(false);
      }
    },
    [selectedSessionId, planTotalVersions, showToast, t],
  );

  // --- Session / sidebar ---
  const refreshSessions = useCallback(async () => {
    try {
      const response = await listThreads();
      const threads = Array.isArray(response?.threads) ? response.threads : [];
      setAiSessions(threadsToSessions(threads));
    } catch (err) {
      console.error("Failed to refresh AI chat sessions:", err);
    }
  }, []);

  const handleSelectSession = useCallback(
    async (sessionId) => {
      try {
        // Clear revert preview when switching sessions
        setIsRevertPreview(false);
        setRevertSourceIndex(null);
        setPreRevertCanvas(null);
        setSessionLoading(true);
        // Get state with messages, canvas, and user messages (for checkpoint_id)
        const [canvasResponse, stateResponse, userMessagesResponse] =
          await Promise.all([
            getThreadCanvas(sessionId),
            getThreadState(sessionId, true), // includeMessages = true
            getThreadMessages(sessionId), // Get user messages with checkpoint_id
          ]);

        const backendMeta = extractTopologyMeta(stateResponse?.topology_meta);
        setCurrentTopology(backendMeta);

        setSelectedSessionId(sessionId);

        // Convert state messages to chat format with messageIndex
        const messages = stateResponse?.messages || [];
        // Response format: { thread_id, user_messages: [{ message_index, content, checkpoint_id, timestamp }], total_count }
        const userMessages = Array.isArray(userMessagesResponse?.user_messages)
          ? userMessagesResponse.user_messages
          : [];

        // Build a queue of checkpoint_ids ordered by user message appearance.
        // We match by order (Nth user message in backend = Nth user message in frontend)
        // rather than by index, because backend uses filtered indices while
        // stateResponse.messages may include system messages that shift positions.
        const checkpointQueue = userMessages
          .filter((um) => um && um.checkpoint_id != null)
          .sort((a, b) => (a.message_index ?? 0) - (b.message_index ?? 0))
          .map((um) => um.checkpoint_id);

        let userMsgCounter = 0;
        const processedMessages = messages
          .map((m, index) => {
            // Convert API message to internal format and add messageIndex
            const message = fromApiMessage(m, "threadState");
            if (message) {
              message.messageIndex = index;
              // Match checkpoint_id by order of user messages (not by raw index)
              if (message.role === "user") {
                if (userMsgCounter < checkpointQueue.length) {
                  message.checkpoint_id = checkpointQueue[userMsgCounter];
                }
                userMsgCounter++;
              }
            }
            return message;
          })
          .filter((m) => {
            if (!m) return false;
            if (
              m.role === "assistant" &&
              typeof m.content === "string" &&
              m.content.trim().startsWith("THINK")
            ) {
              return false;
            }
            // Filter out assistant messages that start with a fully capitalized word
            if (
              m.role === "assistant" &&
              chatMode !== "ask" &&
              startsWithFullyCapitalizedWord(m.content)
            ) {
              // console.log("Filtering out assistant message starting with fully capitalized word:", m.content);
              return false;
            }
            return true;
          });

        // Tag plan messages with planOptions (view-only for historical, active for pending)
        tagPlanMessages(processedMessages, {
          has_interrupt: stateResponse?.has_interrupt,
          plan_status: stateResponse?.plan_status,
          mode: stateResponse?.mode,
        });

        setChatHistory(processedMessages);

        // Extract nodes and edges directly from canvas response
        // Response format: { nodes: [...], edges: [...] }
        const canvasNodes = canvasResponse?.nodes || [];
        const canvasEdges = canvasResponse?.edges || [];

        console.log(
          "=== Canvas JSON from getThreadCanvas (handleSelectSession) ===",
        );
        console.log("sessionId:", sessionId);
        console.log("canvasResponse:", canvasResponse);
        console.log(
          "canvasNodes count:",
          Array.isArray(canvasNodes) ? canvasNodes.length : 0,
        );
        console.log(
          "canvasEdges count:",
          Array.isArray(canvasEdges) ? canvasEdges.length : 0,
        );

        if (canvasNodes.length > 0 || canvasEdges.length > 0) {
          const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
            canvasResponse,
            { layoutDirection, typeToCategory },
          );
          setNodes(rfNodes);
          setTimeout(() => setEdges(rfEdges), 0);
          setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
          setHasUnsyncedChanges(false);
        } else {
          setCurrentTopology(null);
          const emptyCanvas = { nodes: [], edges: [] };
          setNodes([]);
          setEdges([]);
          setCanonicalCanvas(emptyCanvas);
          setHasUnsyncedChanges(false);
        }
      } catch (err) {
        console.error("Failed to load session:", err);
      } finally {
        setSessionLoading(false);
      }
    },
    [layoutDirection, typeToCategory, nodes, chatMode],
  );

  const hasRestoredSession = useRef(false);

  useEffect(() => {
    if (initialThreadsLoading) return;
    if (hasRestoredSession.current) return;
    hasRestoredSession.current = true;

    const storedId = localStorage.getItem("topology_selected_session_id");
    if (!storedId) return;

    const exists = aiSessions.some((s) => s.id === storedId);
    if (exists) {
      handleSelectSession(storedId);
    } else {
      localStorage.removeItem("topology_selected_session_id");
      setSelectedSessionId(null);
    }
  }, [initialThreadsLoading, aiSessions, handleSelectSession]);

  const handleResetChat = useCallback(async () => {
    // console.log("=== handleResetChat - Clearing chat history ===");
    setSelectedSessionId(null);
    localStorage.removeItem("topology_selected_session_id"); // ← ADD THIS LINE

    setChatHistory([]);
    setChatMessage("");
    setCurrentTopology(null);
    setNodes([]);
    setEdges([]);
    setCanonicalCanvas(null);
    setHasUnsyncedChanges(false);
  }, []);

  const handleDeleteSession = useCallback((sessionId, e) => {
    e?.stopPropagation(); // Prevent selecting the session when clicking delete
    setDeleteConfirmState({ show: true, sessionId, loading: false });
  }, []);

  const handleDeleteConfirmClose = useCallback(() => {
    setDeleteConfirmState({ show: false, sessionId: null, loading: false });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const sessionId = deleteConfirmState.sessionId;
    if (!sessionId) return;

    setDeleteConfirmState((prev) => ({ ...prev, loading: true }));

    try {
      await deleteThread(sessionId);
      // Clear selected session if it was the one being deleted
      if (selectedSessionId === sessionId) {
        // console.log("=== handleDeleteConfirm - Clearing chat history (session deleted) ===");
        setSelectedSessionId(null);
        setChatHistory([]);
        setCurrentTopology(null);
        setNodes([]);
        setEdges([]);
      }
      // Refresh sessions list
      await refreshSessions();
      setDeleteConfirmState({ show: false, sessionId: null, loading: false });
    } catch (err) {
      console.error("Failed to delete chat session:", err);
      setDeleteConfirmState({ show: false, sessionId: null, loading: false });
    }
  }, [deleteConfirmState.sessionId, selectedSessionId, refreshSessions]);

  // --- Chat / AI ---
  const handleChatSubmit = useCallback(
    async (e, options = {}) => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }

      // Auto-exit revert preview when sending a new message
      if (isRevertPreview) {
        setIsRevertPreview(false);
        setRevertSourceIndex(null);
        setPreRevertCanvas(null);
      }

      // Downgrade plan action buttons to view-only to prevent concurrent
      // graph operations, but keep the "view plan" marker so the button stays
      setChatHistory((prev) =>
        prev.map((m) =>
          m.planOptions && m.planOptions.length > 1
            ? { ...m, planOptions: ["view"], planStatus: "built" }
            : m,
        ),
      );
      const overrideMessage = options.message;
      const overrideMode = options.mode;
      const canvasOverride = options.canvasOverride || null;

      const baseMessage =
        typeof overrideMessage === "string"
          ? overrideMessage
          : chatMessage.trim();
      if (!baseMessage) return;
      stoppedByUserRef.current = false;

      setAiLoading(true);
      try {
        // Create thread if none exists
        let threadId = selectedSessionId;
        if (!threadId) {
          const newThread = await createThread();
          threadId = newThread.thread_id || newThread.id;
          setSelectedSessionId(threadId);
        }

        const canvasTopology =
          canvasOverride || reactFlowToTopology(nodes, edges, currentTopology);

        console.log("=== Canvas Topology Before Chat Submit ===");
        console.log("canvasTopology:", canvasTopology);
        console.log(
          "canvasTopology.nodes count:",
          canvasTopology?.nodes?.length || 0,
        );
        console.log(
          "canvasTopology.edges count:",
          canvasTopology?.edges?.length || 0,
        );
        console.log(
          "canvasTopology JSON:",
          JSON.stringify(canvasTopology, null, 2),
        );

        // Get checkpoint_id from the last user message in chatHistory
        const lastUserMessage = [...chatHistory]
          .reverse()
          .find((m) => m && m.role === "user");
        const checkpointId = lastUserMessage?.checkpoint_id || null;

        // Add user message to history immediately
        const userMessage = createMessage({
          role: "user",
          content: baseMessage,
          checkpoint_id: checkpointId,
        });
        setChatHistory((prev) => [...prev, userMessage]);

        // Create abort controller for cancellation
        abortControllerRef.current = new AbortController();
        const abortController = abortControllerRef.current;

        // Try streaming first, fallback to blocking request
        let streamSuccess = false;
        let finalData = null;

        try {
          // Reset streaming refs
          streamingAnswerRef.current = "";
          streamingMessageIndexRef.current = null;

          // Handle streaming events
          await sendChatMessageStream(
            threadId,
            baseMessage,
            overrideMode || chatMode || "agent",
            canvasTopology,
            null, // imageUrl
            checkpointId,
            (event) => {
              // Handle different event types
              if (event.type === "meta") {
                // Stream started - clear history
                streamSuccess = true;
                setStreamingStatusHistory([]);
                setIterationSnapshots([]);
                const initialStatus = {
                  phase: "started",
                  message: "Starting...",
                  timestamp: new Date().toISOString(),
                  id: Date.now(),
                };
                setStreamingStatus(initialStatus);
                setStreamingStatusHistory([initialStatus]);
              } else if (event.type === "status") {
                // Update status message and add to history
                const statusMessage = event.message || `${event.phase}...`;
                const statusEntry = {
                  phase: event.phase || "processing",
                  message: statusMessage,
                  node: event.node,
                  timestamp: new Date().toISOString(),
                  id: Date.now() + Math.random(), // Unique ID
                };

                // Update current status
                setStreamingStatus(statusEntry);

                // Add to history (keep last 15 entries)
                setStreamingStatusHistory((prev) => {
                  const updated = [...prev, statusEntry];
                  return updated.slice(-15); // Keep last 15 status updates
                });
              } else if (event.type === "delta") {
                // Accumulate streaming tokens
                if (event.channel === "answer") {
                  streamingAnswerRef.current += event.content;
                  // Capture the current accumulated content to avoid closure issues
                  const currentContent = streamingAnswerRef.current;

                  // Update or create assistant message with streaming content
                  // Use a function update to ensure we get the latest state
                  setChatHistory((prev) => {
                    // Create a completely new array to ensure React detects the change
                    const newHistory = prev.map((msg) => ({ ...msg }));
                    const currentIndex = streamingMessageIndexRef.current;

                    // Find or create assistant message
                    if (currentIndex === null) {
                      // Always create a new assistant message after the user input
                      const assistantMsg = createMessage({
                        role: "assistant",
                        content: currentContent,
                        isStreaming: true,
                      });
                      streamingMessageIndexRef.current = newHistory.length;
                      newHistory.push(assistantMsg);
                    } else {
                      // Update existing assistant message
                      if (newHistory[currentIndex]) {
                        newHistory[currentIndex] = {
                          ...newHistory[currentIndex],
                          content: currentContent,
                          isStreaming: true,
                        };
                      } else {
                        // Index is out of bounds, create new message
                        const assistantMsg = createMessage({
                          role: "assistant",
                          content: currentContent,
                          isStreaming: true,
                        });
                        streamingMessageIndexRef.current = newHistory.length;
                        newHistory.push(assistantMsg);
                      }
                    }
                    // Return new array to ensure React detects the change
                    return newHistory;
                  });
                }
              } else if (event.type === "iteration") {
                // Per-iteration validation snapshot from agent mode
                setIterationSnapshots((prev) => [
                  ...prev,
                  {
                    iteration: event.iteration,
                    maxIterations: event.max_iterations,
                    valid: event.it_valid,
                    reason: event.it_reason || "",
                    reasonHeadline: event.reason_headline || "",
                    otFindings: event.ot_findings || [],
                    otCriticalCount: event.ot_critical_count || 0,
                    isFinal: event.is_final_iteration,
                    nodeCount: event.canvas_json?.nodes?.length || 0,
                    edgeCount: event.canvas_json?.edges?.length || 0,
                    timestamp: new Date().toISOString(),
                  },
                ]);
              } else if (event.type === "iteration_preview") {
                // Think node finished — show what the agent is planning
                const previewMsg = event.headline
                  ? `Iteration ${event.iteration}/${event.max_iterations}: ${event.headline}`
                  : `Iteration ${event.iteration}/${event.max_iterations}: Building topology...`;
                const statusEntry = {
                  phase: "applying",
                  message: previewMsg,
                  timestamp: new Date().toISOString(),
                  id: Date.now() + Math.random(),
                };
                setStreamingStatus(statusEntry);
                setStreamingStatusHistory((prev) =>
                  [...prev, statusEntry].slice(-15),
                );
              } else if (event.type === "final") {
                // Stream complete, process final data
                finalData = {
                  messages: event.messages || [],
                  canvas_json: event.canvas_json,
                  checkpoint_id: event.checkpoint_id,
                  interrupt_data: event.interrupt_data, // Include interrupt_data for plan mode
                  plan_status: event.plan_status, // Include plan_status for plan mode
                  topology_meta: event.topology_meta,
                };
                setStreamingStatus(null);
                setStreamingStatusHistory([]);
              } else if (event.type === "error") {
                // Stream error
                setStreamingStatus(null);
                setStreamingStatusHistory([]);
                throw new Error(event.message || "Stream error occurred");
              }
            },
            abortController,
          );

          // If streaming succeeded, process final data
          if (streamSuccess && finalData) {
            // Process final messages similar to blocking request
            if (finalData.messages && Array.isArray(finalData.messages)) {
              // Get current chat history to preserve streaming message
              setChatHistory((prevHistory) => {
                // Build checkpoint queue from previous history (counter-based, not index-based)
                const prevCheckpoints = [];
                for (const m of prevHistory) {
                  if (m && m.role === "user" && m.checkpoint_id) {
                    prevCheckpoints.push(m.checkpoint_id);
                  }
                }
                let ckptCounter = 0;

                let newMessages = finalData.messages
                  .map((msg, index) => {
                    const message = fromApiMessage(msg, "chatResponse");
                    if (message) {
                      message.messageIndex = index;
                      if (message.role === "user") {
                        if (ckptCounter < prevCheckpoints.length) {
                          message.checkpoint_id = prevCheckpoints[ckptCounter];
                        }
                        ckptCounter++;
                      }
                    }
                    return message;
                  })
                  .filter((msg) => {
                    if (!msg) return false;
                    if (
                      msg.role === "assistant" &&
                      typeof msg.content === "string" &&
                      msg.content.trim().startsWith("THINK")
                    ) {
                      return false;
                    }
                    if (
                      msg.role === "assistant" &&
                      chatMode !== "ask" &&
                      startsWithFullyCapitalizedWord(msg.content)
                    ) {
                      return false;
                    }
                    return true;
                  });

                // If we have a streaming message, merge it with final messages
                const streamingIdx = streamingMessageIndexRef.current;
                if (streamingIdx !== null && prevHistory[streamingIdx]) {
                  const streamingMsg = prevHistory[streamingIdx];
                  // Find the corresponding assistant message in newMessages
                  const lastAssistantInNew = newMessages
                    .map((m, i) => (m?.role === "assistant" ? i : -1))
                    .filter((i) => i >= 0)
                    .pop();

                  if (
                    lastAssistantInNew !== undefined &&
                    newMessages[lastAssistantInNew]
                  ) {
                    // Merge streaming content with final message (final takes precedence if different)
                    const finalContent =
                      newMessages[lastAssistantInNew].content;
                    const streamingContent = streamingAnswerRef.current;
                    // Use final content if available, otherwise use streaming content
                    newMessages[lastAssistantInNew].content =
                      finalContent || streamingContent;
                    newMessages[lastAssistantInNew].isStreaming = false; // Mark as complete
                  } else {
                    // No assistant message in final, use streaming message
                    const completedStreamingMsg = {
                      ...streamingMsg,
                      content:
                        streamingAnswerRef.current || streamingMsg.content,
                      isStreaming: false,
                    };
                    newMessages.push(completedStreamingMsg);
                  }
                }

                // Assign checkpoint_id
                if (finalData.checkpoint_id) {
                  const lastUserIdx = newMessages
                    .map((m, i) => (m?.role === "user" ? i : -1))
                    .filter((i) => i >= 0)
                    .pop();
                  if (lastUserIdx != null && newMessages[lastUserIdx]) {
                    newMessages[lastUserIdx].checkpoint_id =
                      finalData.checkpoint_id;
                  }
                }

                // Attach iteration snapshots to the last assistant message
                if (iterationSnapshots.length > 0) {
                  const lastAssistantIdx = newMessages
                    .map((m, i) => (m?.role === "assistant" ? i : -1))
                    .filter((i) => i >= 0)
                    .pop();
                  if (
                    lastAssistantIdx != null &&
                    newMessages[lastAssistantIdx]
                  ) {
                    newMessages[lastAssistantIdx].iterationSnapshots =
                      iterationSnapshots;
                  }
                }

                // If this was a PLAN interrupt, append a message with plan details and options
                if (chatMode === "plan" && finalData.interrupt_data) {
                  const interrupt = finalData.interrupt_data;
                  const options = Array.isArray(interrupt.options)
                    ? interrupt.options
                    : [];

                  // Use streamed content if available (what user actually saw), otherwise use interrupt.plan_md
                  const streamedPlanContent = streamingAnswerRef.current.trim();
                  const planContent =
                    streamedPlanContent ||
                    interrupt.plan_md ||
                    interrupt.instruction ||
                    "";

                  if (planContent || options.length > 0) {
                    const planMessage = createMessage({
                      role: "assistant",
                      content: planContent || "Plan options:",
                      metadata: {
                        plan_status:
                          finalData.plan_status || interrupt.status || null,
                        interrupt_type: interrupt.type || "plan",
                      },
                      planOptions: options,
                      planStatus: interrupt.status || finalData.plan_status,
                      interruptType: interrupt.type,
                      instruction: interrupt.instruction,
                      checkpoint_id:
                        finalData.checkpoint_id ||
                        interrupt.checkpoint_id ||
                        null,
                    });
                    planMessage.messageIndex = newMessages.length;
                    newMessages.push(planMessage);
                  }
                }

                return newMessages;
              });

              // Update canvas if provided (for streaming path)
              const streamMeta = extractTopologyMeta(finalData.topology_meta);
              if (streamMeta) {
                setCurrentTopology(streamMeta);
              }

              if (finalData.canvas_json && chatMode === "agent") {
                const canvasData = finalData.canvas_json;
                const canvasNodes = canvasData?.nodes || [];
                const canvasEdges = canvasData?.edges || [];

                if (canvasNodes.length > 0 || canvasEdges.length > 0) {
                  const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
                    canvasData,
                    { layoutDirection, typeToCategory },
                  );
                  setNodes(rfNodes);
                  setTimeout(() => setEdges(rfEdges), 0);
                  setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
                  setHasUnsyncedChanges(false);
                }
              }
            }
          }
        } catch (streamError) {
          if (stoppedByUserRef.current) {
            // User intentionally stopped — do not fall back to blocking request
            console.log("[Stream] Stopped by user, skipping fallback");
          } else {
            console.warn(
              "Streaming failed, falling back to blocking request:",
              streamError,
            );
            // Fallback to blocking request
            const data = await sendChatMessage(
              threadId,
              baseMessage,
              overrideMode || chatMode || "agent",
              canvasTopology,
              null, // imageUrl
              checkpointId,
            );
            finalData = data;
          }
        }

        // Process final data if we have it (from fallback)
        if (!streamSuccess && finalData) {
          const data = finalData;
          const responseMeta = extractTopologyMeta(data?.topology_meta);
          if (responseMeta) {
            setCurrentTopology(responseMeta);
          }

          if (data.messages && Array.isArray(data.messages)) {
            // Convert API messages to chat format using utility functions
            // Filter out any empty, invalid, or assistant messages that start with capitalized word
            // Build checkpoint queue from current history (counter-based, not index-based)
            const prevCheckpoints = [];
            for (const m of chatHistory) {
              if (m && m.role === "user" && m.checkpoint_id) {
                prevCheckpoints.push(m.checkpoint_id);
              }
            }
            let ckptCounter = 0;

            let newMessages = data.messages
              .map((msg, index) => {
                const message = fromApiMessage(msg, "chatResponse");
                if (message) {
                  message.messageIndex = index;
                  if (message.role === "user") {
                    if (ckptCounter < prevCheckpoints.length) {
                      message.checkpoint_id = prevCheckpoints[ckptCounter];
                    }
                    ckptCounter++;
                  }
                }
                return message;
              })
              .filter((msg) => {
                if (!msg) {
                  return false;
                }

                if (
                  msg.role === "assistant" &&
                  typeof msg.content === "string" &&
                  msg.content.trim().startsWith("THINK")
                ) {
                  return false;
                }

                // Filter out assistant messages that start with a fully capitalized word
                if (
                  msg.role === "assistant" &&
                  chatMode !== "ask" &&
                  startsWithFullyCapitalizedWord(msg.content)
                ) {
                  return false;
                }

                return true;
              });

            // Assign response checkpoint_id to the last user message (newly sent message) so context menu works
            if (data.checkpoint_id) {
              const lastUserIdx = newMessages
                .map((m, i) => (m?.role === "user" ? i : -1))
                .filter((i) => i >= 0)
                .pop();
              if (lastUserIdx != null && newMessages[lastUserIdx]) {
                newMessages[lastUserIdx].checkpoint_id = data.checkpoint_id;
              }
            }

            // If this was a PLAN interrupt, append a message with plan details and options
            if (chatMode === "plan" && data.interrupt_data) {
              const interrupt = data.interrupt_data;
              const options = Array.isArray(interrupt.options)
                ? interrupt.options
                : [];
              const planContent =
                interrupt.plan_md || interrupt.instruction || "";

              if (planContent || options.length > 0) {
                const planMessage = createMessage({
                  role: "assistant",
                  content: planContent || "Plan options:",
                  metadata: {
                    plan_status: data.plan_status || interrupt.status || null,
                    interrupt_type: interrupt.type || "plan",
                  },
                  planOptions: options,
                  planStatus: interrupt.status || data.plan_status,
                  interruptType: interrupt.type,
                  instruction: interrupt.instruction,
                  checkpoint_id:
                    data.checkpoint_id || interrupt.checkpoint_id || null,
                });
                planMessage.messageIndex = newMessages.length;
                newMessages = [...newMessages, planMessage];
              }
            }

            if (newMessages.length > 0) {
              // Replace entire history with backend's complete message list
              setChatHistory(newMessages);
            } else {
              // If no valid messages, append user message and placeholder
              console.warn("No valid messages in response, appending fallback");
              setChatHistory((prev) => {
                const userContent = baseMessage;
                return [
                  ...prev,
                  { role: "user", content: userContent },
                  { role: "assistant", content: "[No response content]" },
                ];
              });
            }
          } else {
            // Fallback: append user message and placeholder if no messages array in response
            console.warn("No messages array in response, appending fallback");
            setChatHistory((prev) => {
              const userContent = baseMessage;
              return [
                ...prev,
                { role: "user", content: userContent },
                {
                  role: "assistant",
                  content: "[Response received - no messages in response]",
                },
              ];
            });
          }
        } // Close if (!streamSuccess && finalData) block

        // Update canvas from canvas_json only for agent mode (for both streaming and fallback)
        const canvasData =
          streamSuccess && finalData?.canvas_json
            ? finalData.canvas_json
            : !streamSuccess && finalData?.canvas_json
              ? finalData.canvas_json
              : null;

        if (chatMode === "agent" && canvasData) {
          const canvasNodes = canvasData?.nodes || [];
          const canvasEdges = canvasData?.edges || [];

          if (canvasNodes.length > 0 || canvasEdges.length > 0) {
            const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
              canvasData,
              { layoutDirection, typeToCategory },
            );
            setNodes(rfNodes);
            setTimeout(() => setEdges(rfEdges), 0);
            setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
            setHasUnsyncedChanges(false);
          }
        }

        await refreshSessions();
      } catch (err) {
        console.error("Error talking to AI topology backend:", err);
        showToast(err?.message || "Failed to send message", "error");
      } finally {
        abortControllerRef.current = null; // clear ref so GC can collect it
        stoppedByUserRef.current = false; // reset flag for next interaction
        setAiLoading(false);
        setStreamingStatus(null);
        setStreamingStatusHistory([]);
        // Only clear chat input when using the text box, not for programmatic calls
        if (!options.skipClearInput) {
          setChatMessage("");
        }
      }
    },
    [
      chatMessage,
      nodes,
      edges,
      currentTopology,
      selectedSessionId,
      refreshSessions,
      showToast,
      chatMode,
      chatHistory,
      layoutDirection,
      typeToCategory,
      isRevertPreview,
    ],
  );
  const handleStopGeneration = useCallback(async () => {
    if (!selectedSessionId) return;

    // 1. Set flag FIRST — so downstream catch blocks see it synchronously
    stoppedByUserRef.current = true;

    // 2. Signal backend to stop (sets asyncio.Event server-side)
    try {
      await stopChatGeneration(selectedSessionId);
    } catch (err) {
      console.error("[Stop] Failed to signal backend:", err);
    }

    // 3. Abort the client-side SSE fetch — reader.read() will throw AbortError
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, [selectedSessionId]);

  const handleImportChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const isJsonFile =
        (file.type && file.type.includes("json")) ||
        (file.name && file.name.toLowerCase().endsWith(".json"));

      if (!isJsonFile) {
        // Ignore non-JSON imports for now
        e.target.value = "";
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        try {
          const parsed = JSON.parse(reader.result);
          const importedMeta = extractTopologyMeta(parsed);
          if (importedMeta) {
            setCurrentTopology(importedMeta);
          }

          let rfNodes = [];
          let rfEdges = [];

          // Case 1: React Flow-style JSON { nodes, edges }
          if (Array.isArray(parsed?.nodes) && Array.isArray(parsed?.edges)) {
            rfNodes = validateNodes(parsed.nodes);
            rfEdges = parsed.edges || [];
          } else {
            // Case 2: canonical-style { nodes, edges } that need converting
            const topologyData = parsed || {};
            if (!Array.isArray(topologyData.nodes)) topologyData.nodes = [];
            if (!Array.isArray(topologyData.edges)) topologyData.edges = [];

            const converted = topologyToReactFlow(topologyData, nodes);
            rfNodes = validateNodes(converted.nodes || []);
            rfEdges = converted.edges || [];
          }

          // Load into canvas
          setNodes(rfNodes);
          setEdges(rfEdges);

          // Convert imported canvas to canonical topology and reuse chat submit pipeline
          const canvasTopology = reactFlowToTopology(
            rfNodes,
            rfEdges,
            currentTopology,
          );
          handleChatSubmit(null, {
            message: "I just imported a topology from a JSON file",
            mode: "ask",
            canvasOverride: canvasTopology,
            skipClearInput: true,
          });
        } catch (err) {
          console.error("Failed to import topology from JSON:", err);
          showToast(
            err?.message || t("Failed to import topology from JSON"),
            "error",
          );
        } finally {
          e.target.value = "";
        }
      };

      reader.readAsText(file);
    },
    [nodes, currentTopology, handleChatSubmit, t, showToast],
  );

  const handleImportJsonFromModal = useCallback(
    async (file) => {
      const isJsonFile =
        (file.type && file.type.includes("json")) ||
        (file.name && file.name.toLowerCase().endsWith(".json"));
      if (!isJsonFile) {
        showToast(t("Please select a valid JSON file"), "warning");
        return;
      }

      setJsonUploading(true);
      try {
        try {
          const parsed = JSON.parse(await file.text());
          const importedMeta = extractTopologyMeta(parsed);
          if (importedMeta) {
            setCurrentTopology(importedMeta);
          }
        } catch {
          // Ignore metadata extraction errors and continue with backend import.
        }

        // Ensure we have a thread
        let threadId = selectedSessionId;
        if (!threadId) {
          const newThread = await createThread();
          threadId = newThread.thread_id || newThread.id;
          setSelectedSessionId(threadId);
        }

        const data = await importTopologyJson(threadId, file);
        const importedResponseMeta = extractTopologyMeta(data?.topology_meta);
        if (importedResponseMeta) {
          setCurrentTopology(importedResponseMeta);
        }

        // Apply canvas from response
        if (data?.canvas_json) {
          const canvasData = data.canvas_json;
          const canvasNodes = canvasData?.nodes || [];
          const canvasEdges = canvasData?.edges || [];

          if (canvasNodes.length > 0 || canvasEdges.length > 0) {
            const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
              canvasData,
              { layoutDirection, typeToCategory },
            );
            setNodes(rfNodes);
            setTimeout(() => setEdges(rfEdges), 0);
            setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
            setHasUnsyncedChanges(false);
          }
        }

        // Reload session to pick up messages (import endpoint returns canvas only)
        await refreshSessions();
        await handleSelectSession(threadId);
        setImportModalOpen(false);
        showToast(t("Topology imported successfully"), "success");
      } catch (err) {
        console.error("Failed to import topology from JSON:", err);
        showToast(
          err?.message || t("Failed to import topology from JSON"),
          "error",
        );
      } finally {
        setJsonUploading(false);
      }
    },
    [
      selectedSessionId,
      layoutDirection,
      typeToCategory,
      refreshSessions,
      handleSelectSession,
      showToast,
      t,
    ],
  );

  const handleImportImageFromModal = useCallback(
    async (imageFile) => {
      setImageUploading(true);
      try {
        let threadId = selectedSessionId;
        if (!threadId) {
          const newThread = await createThread();
          threadId = newThread.thread_id || newThread.id;
          setSelectedSessionId(threadId);
        }

        const canvasTopology = reactFlowToTopology(
          nodes,
          edges,
          currentTopology,
        );

        const data = await uploadTopologyImage(
          threadId,
          "vision",
          "Extract the topology from this image",
          imageFile,
          canvasTopology,
        );
        const imageResponseMeta = extractTopologyMeta(data?.topology_meta);
        if (imageResponseMeta) {
          setCurrentTopology(imageResponseMeta);
        }

        if (data?.canvas_json) {
          const canvasData = data.canvas_json;
          const canvasNodes = canvasData?.nodes || [];
          const canvasEdges = canvasData?.edges || [];

          if (canvasNodes.length > 0 || canvasEdges.length > 0) {
            const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
              canvasData,
              { layoutDirection, typeToCategory },
            );
            setNodes(rfNodes);
            setTimeout(() => setEdges(rfEdges), 0);
            setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
            setHasUnsyncedChanges(false);
          }
        }

        if (Array.isArray(data?.messages) && data.messages.length > 0) {
          const newMessages = data.messages.map((m, idx) =>
            fromApiMessage(m, idx),
          );
          // Fetch checkpoint_ids for all user messages from backend
          try {
            const umResponse = await getThreadMessages(threadId);
            const userMsgs = Array.isArray(umResponse?.user_messages)
              ? umResponse.user_messages
              : [];
            const ckptQueue = userMsgs
              .filter((um) => um && um.checkpoint_id != null)
              .sort((a, b) => (a.message_index ?? 0) - (b.message_index ?? 0))
              .map((um) => um.checkpoint_id);
            let uc = 0;
            for (const m of newMessages) {
              if (m && m.role === "user" && uc < ckptQueue.length) {
                m.checkpoint_id = ckptQueue[uc];
                uc++;
              }
            }
          } catch (e) {
            // Fallback: at least attach the latest checkpoint_id
            if (data.checkpoint_id) {
              const lastUserIdx = newMessages
                .map((m, i) => (m?.role === "user" ? i : -1))
                .filter((i) => i >= 0)
                .pop();
              if (lastUserIdx != null && newMessages[lastUserIdx]) {
                newMessages[lastUserIdx].checkpoint_id = data.checkpoint_id;
              }
            }
          }
          setChatHistory(newMessages);
        }

        await refreshSessions();
        setImportModalOpen(false);
        showToast(t("Topology extracted from image successfully"), "success");
      } catch (err) {
        console.error("Failed to upload topology image:", err);
        showToast(
          err?.message || t("Failed to process topology image"),
          "error",
        );
      } finally {
        setImageUploading(false);
      }
    },
    [
      selectedSessionId,
      nodes,
      edges,
      currentTopology,
      layoutDirection,
      typeToCategory,
      refreshSessions,
      showToast,
      t,
    ],
  );

  const handlePlanOptionSelect = useCallback(
    async (option) => {
      if (!selectedSessionId) return;
      const normalized = (option || "").toLowerCase();
      if (!normalized) return;
      // "Revise" opens a modal for feedback; submit from modal calls resume with user_feedback
      if (normalized === "revise") {
        setPlanActionState({ loading: true, option: "revise" });
        setReviseModalOpen(true);
        return;
      }

      setPlanActionState({ loading: true, option: normalized });
      setAiLoading(true);
      try {
        const data = await resumeChatMessage(
          selectedSessionId,
          normalized,
          null,
        );

        if (data.messages && Array.isArray(data.messages)) {
          let newMessages = data.messages
            .map((msg, index) => {
              const message = fromApiMessage(msg, "resumeResponse");
              if (message) {
                message.messageIndex = index;
              }
              return message;
            })
            .filter((msg) => {
              if (!msg) return false;
              if (
                msg.role === "assistant" &&
                startsWithFullyCapitalizedWord(msg.content)
              ) {
                return false;
              }
              return true;
            });

          // Remove only the LAST "Plan generated/revised" message to avoid
          // duplication with the frontend planMessage created from interrupt_data.
          // Previous plan messages from earlier cycles must be preserved.
          {
            let lastPlanIdx = -1;
            for (let i = newMessages.length - 1; i >= 0; i--) {
              const msg = newMessages[i];
              if (
                msg?.role === "assistant" &&
                typeof msg.content === "string" &&
                /plan (generated|revised)/i.test(msg.content)
              ) {
                lastPlanIdx = i;
                break;
              }
            }
            if (lastPlanIdx >= 0) {
              newMessages.splice(lastPlanIdx, 1);
            }
          }

          if (data.interrupt_data) {
            const interrupt = data.interrupt_data;
            const options = Array.isArray(interrupt.options)
              ? interrupt.options
              : [];
            const planContent =
              interrupt.plan_md || interrupt.instruction || "";

            if (planContent || options.length > 0) {
              const planMessage = createMessage({
                role: "assistant",
                content: planContent || "Plan options:",
                metadata: {
                  plan_status: data.plan_status || interrupt.status || null,
                  interrupt_type: interrupt.type || "plan",
                },
                planOptions: options,
                planStatus: interrupt.status || data.plan_status,
                interruptType: interrupt.type,
                instruction: interrupt.instruction,
                checkpoint_id:
                  data.checkpoint_id || interrupt.checkpoint_id || null,
              });
              planMessage.messageIndex = newMessages.length;
              newMessages = [...newMessages, planMessage];
            }
          }

          // Re-fetch checkpoint_ids for all user messages
          try {
            const umResponse = await getThreadMessages(selectedSessionId);
            const userMsgs = Array.isArray(umResponse?.user_messages)
              ? umResponse.user_messages
              : [];
            const ckptQueue = userMsgs
              .filter((um) => um && um.checkpoint_id != null)
              .sort((a, b) => (a.message_index ?? 0) - (b.message_index ?? 0))
              .map((um) => um.checkpoint_id);
            let uc = 0;
            for (const m of newMessages) {
              if (m && m.role === "user" && uc < ckptQueue.length) {
                m.checkpoint_id = ckptQueue[uc];
                uc++;
              }
            }
          } catch (_) {}

          // Re-tag planOptions on historical plan messages so view plan buttons persist
          tagPlanMessages(newMessages, {
            has_interrupt: !!data.interrupt_data,
            plan_status: data.plan_status,
            mode: "plan",
          });

          if (newMessages.length > 0) {
            setChatHistory(newMessages);
          }
        }

        if (data.canvas_json) {
          const canvasData = data.canvas_json;
          const canvasNodes = canvasData?.nodes || [];
          const canvasEdges = canvasData?.edges || [];

          if (canvasNodes.length > 0 || canvasEdges.length > 0) {
            const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
              canvasData,
              { layoutDirection, typeToCategory },
            );
            setNodes(rfNodes);
            setTimeout(() => setEdges(rfEdges), 0);
            setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
            setHasUnsyncedChanges(false);
          }
        }

        await refreshSessions();
      } catch (err) {
        if (!stoppedByUserRef.current) {
          console.error("Error resuming plan:", err);
          showToast(err?.message || "Failed to resume plan", "error");
        }
      } finally {
        setPlanActionState({ loading: false, option: null });
        setAiLoading(false);
      }
    },
    [
      layoutDirection,
      typeToCategory,
      selectedSessionId,
      refreshSessions,
      showToast,
    ],
  );

  const handleReviseSubmit = useCallback(
    async (feedbackFromModal) => {
      if (!selectedSessionId) return;
      const feedback = (
        typeof feedbackFromModal === "string"
          ? feedbackFromModal
          : reviseFeedback || ""
      ).trim();
      setAiLoading(true);
      try {
        const data = await resumeChatMessage(
          selectedSessionId,
          "revise",
          feedback || null,
        );

        if (data.messages && Array.isArray(data.messages)) {
          let newMessages = data.messages
            .map((msg, index) => {
              const message = fromApiMessage(msg, "resumeResponse");
              if (message) {
                message.messageIndex = index;
              }
              return message;
            })
            .filter((msg) => {
              if (!msg) return false;
              if (
                msg.role === "assistant" &&
                startsWithFullyCapitalizedWord(msg.content)
              ) {
                return false;
              }
              return true;
            });

          // Remove only the LAST "Plan generated/revised" message to avoid
          // duplication with the frontend planMessage created from interrupt_data.
          // Previous plan messages from earlier cycles must be preserved.
          {
            let lastPlanIdx = -1;
            for (let i = newMessages.length - 1; i >= 0; i--) {
              const msg = newMessages[i];
              if (
                msg?.role === "assistant" &&
                typeof msg.content === "string" &&
                /plan (generated|revised)/i.test(msg.content)
              ) {
                lastPlanIdx = i;
                break;
              }
            }
            if (lastPlanIdx >= 0) {
              newMessages.splice(lastPlanIdx, 1);
            }
          }

          if (data.interrupt_data) {
            const interrupt = data.interrupt_data;
            const options = Array.isArray(interrupt.options)
              ? interrupt.options
              : [];
            const planContent =
              interrupt.plan_md || interrupt.instruction || "";

            if (planContent || options.length > 0) {
              const planMessage = createMessage({
                role: "assistant",
                content: planContent || "Plan options:",
                metadata: {
                  plan_status: data.plan_status || interrupt.status || null,
                  interrupt_type: interrupt.type || "plan",
                },
                planOptions: options,
                planStatus: interrupt.status || data.plan_status,
                interruptType: interrupt.type,
                instruction: interrupt.instruction,
                checkpoint_id:
                  data.checkpoint_id || interrupt.checkpoint_id || null,
              });
              planMessage.messageIndex = newMessages.length;
              newMessages = [...newMessages, planMessage];
            }
          }

          // Re-fetch checkpoint_ids for all user messages
          try {
            const umResponse = await getThreadMessages(selectedSessionId);
            const userMsgs = Array.isArray(umResponse?.user_messages)
              ? umResponse.user_messages
              : [];
            const ckptQueue = userMsgs
              .filter((um) => um && um.checkpoint_id != null)
              .sort((a, b) => (a.message_index ?? 0) - (b.message_index ?? 0))
              .map((um) => um.checkpoint_id);
            let uc = 0;
            for (const m of newMessages) {
              if (m && m.role === "user" && uc < ckptQueue.length) {
                m.checkpoint_id = ckptQueue[uc];
                uc++;
              }
            }
          } catch (_) {}

          // Re-tag planOptions on historical plan messages so view plan buttons persist
          tagPlanMessages(newMessages, {
            has_interrupt: !!data.interrupt_data,
            plan_status: data.plan_status,
            mode: "plan",
          });

          if (newMessages.length > 0) {
            setChatHistory(newMessages);
          }
        }

        if (data.canvas_json) {
          const canvasData = data.canvas_json;
          const canvasNodes = canvasData?.nodes || [];
          const canvasEdges = canvasData?.edges || [];

          if (canvasNodes.length > 0 || canvasEdges.length > 0) {
            const { nodes: rfNodes, edges: rfEdges } = applyCanvasFromApi(
              canvasData,
              { layoutDirection, typeToCategory },
            );
            setNodes(rfNodes);
            setTimeout(() => setEdges(rfEdges), 0);
            setCanonicalCanvas({ nodes: rfNodes, edges: rfEdges });
            setHasUnsyncedChanges(false);
          }
        }

        await refreshSessions();
        setReviseModalOpen(false);
        setReviseFeedback("");
      } catch (err) {
        if (!stoppedByUserRef.current) {
          console.error("Error submitting revise feedback:", err);
          showToast(err?.message || "Failed to submit feedback", "error");
        }
      } finally {
        setPlanActionState({ loading: false, option: null });
        setAiLoading(false);
      }
    },
    [
      layoutDirection,
      typeToCategory,
      selectedSessionId,
      reviseFeedback,
      refreshSessions,
      showToast,
    ],
  );

  const handleReviseModalClose = useCallback(() => {
    setPlanActionState({ loading: false, option: null });
    if (!aiLoading) {
      setReviseModalOpen(false);
      setReviseFeedback("");
    }
  }, [aiLoading]);

  // --- Derived (for JSX) ---
  const deviceTypesMap = useMemo(() => {
    const map = new Map();
    availableNodeTypes.forEach((nt) => {
      map.set(nt.type, {
        type: nt.type,
        label: nt.label,
        icon: nt.icon,
        category: nt.category,
        metadata: nt.metadata ?? {},
      });
    });
    return map;
  }, [availableNodeTypes]);

  const roles = user?.roles || [];
  const isCompanyUser = roles.includes("company_user");
  const isCompanyAdmin = roles.includes("company_admin");
  const isPlatformAdmin = roles.includes("platform_admin");
  const PageLayout = isCompanyUser
    ? CompanyUserLayout
    : isCompanyAdmin
      ? CompanyAdminLayout
      : isPlatformAdmin
        ? AdminLayout
        : Layout;
  const hasSidebarLayout = isCompanyUser || isCompanyAdmin || isPlatformAdmin;

  return (
    <PageLayout>
      <DeviceTypesContext.Provider value={deviceTypesMap}>
        <div className="w-full flex flex-col flex-1">
          {initialThreadsLoading && (
            <FullScreenLoader
              message={t("Loading...")}
              offsetSidebar={hasSidebarLayout}
            />
          )}
          <main className="flex-1 flex flex-col py-8 px-8 max-w-[1450px] mx-auto w-full gap-6">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-(--color-text-primary)">
                    {t("topology.builder_title")}
                  </h1>
                </div>
                <p className="text-xs mt-0.5 text-(--color-text-muted)">
                  {t("topology.builder_description")}
                </p>
              </div>
              <div className="flex justify-end">
                <WalkthroughMenu />
              </div>
            </div>

            {/* Toolbar – TopologyActionsPanel (Import, Preview, Reset, Sync, My Topologies, Submit) */}
            <TopologyActionsPanel
              fileInputRef={fileInputRef}
              onPreviewJson={handlePreviewJson}
              onImportClick={handleImportClick}
              onImportChange={handleImportChange}
              onReset={() => {
                setNodes([]);
                setEdges([]);
                setHasUnsyncedChanges(false);
              }}
              onSubmitTopology={handleSubmitTopologyClick}
              onSyncTopology={handleSyncTopology}
              hasUnsyncedChanges={hasUnsyncedChanges}
              syncLoading={syncLoading}
              savedTopologies={savedTopologies}
              defaultTopologies={defaultTopologies}
              onSavedTopologyClick={handleSavedTopologyClick}
              onDefaultTopologyClick={handleDefaultTopologyClick}
              aiLoading={aiLoading}
              horizontal={true}
              submitLoading={submitState.loading}
              submitSuccess={submitState.success}
              submitError={submitState.error}
              isRevertPreview={isRevertPreview}
              onRevertSave={handleRevertSave}
              onRevertUndo={handleRevertUndo}
            />

            {/* Main Grid Layout */}
            <div
              className="flex-1 grid grid-cols-12 gap-3 min-h-[600px] lg:grid-rows-1"
              style={{ maxHeight: sidebarMaxHeight }}
              dir="ltr"
            >
              {/* Left Sidebar - AI Architect (AIChat component)*/}
              <aside
                className="col-span-12 lg:col-span-3 flex flex-col border border-(--color-border) rounded-2xl overflow-hidden shadow-sm min-h-[600px] max-h-[80vh] lg:max-h-none"
                dir={isRtl ? "rtl" : "ltr"}
              >
                <AIChat
                  sessions={aiSessions}
                  selectedSessionId={selectedSessionId}
                  chatHistory={chatHistory}
                  chatMessage={chatMessage}
                  onMessageChange={setChatMessage}
                  onMessageSubmit={handleChatSubmit}
                  loading={aiLoading && !sessionLoading}
                  sessionLoading={sessionLoading}
                  planActionLoading={planActionState.loading}
                  planActionOption={planActionState.option}
                  streamingStatus={streamingStatus}
                  streamingStatusHistory={streamingStatusHistory}
                  iterationSnapshots={iterationSnapshots}
                  onReset={handleResetChat}
                  title={t("topology.ai.title")}
                  placeholder={t("topology.ai.placeholder")}
                  emptyHistoryText={t("Start by describing a topology, e.g.")}
                  examplePrompts={[
                    t("Create a basic web server topology with load balancer"),
                    t("Design a secure network with firewall and VPN"),
                    t("Add monitoring and logging infrastructure"),
                  ]}
                  embedded={true}
                  chatMode={chatMode}
                  onModeChange={setChatMode}
                  onPlanOptionSelect={handlePlanOptionSelect}
                  onStop={handleStopGeneration}
                  onOpenHistory={() => setChatHistorySidebarOpen(true)}
                  onRevertToMessage={handleRevertToMessage}
                  onViewPlan={handleViewPlan}
                  isRevertPreview={isRevertPreview}
                  revertSourceIndex={revertSourceIndex}
                />
              </aside>

              {/* Center Canvas */}
              <section
                className="col-span-12 lg:col-span-6 bg-(--color-bg-card) border border-(--color-border) rounded-2xl relative overflow-hidden shadow-sm group"
                dir={isRtl ? "rtl" : "ltr"}
              >
                <div
                  className="absolute inset-0 opacity-50"
                  style={{
                    backgroundImage:
                      "radial-gradient(#334155 1px, transparent 1px)",
                    backgroundSize: "24px 24px",
                  }}
                ></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-4 text-gray-500 group-hover:scale-105 transition-transform duration-700">
                  <svg
                    className="w-24 h-24 opacity-20"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={0.5}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 7.5l-2.25-1.313M21 7.5v2.25m0-2.25l-2.25 1.313M3 7.5l2.25-1.313M3 7.5l2.25 1.313M3 7.5v2.25m9 3l2.25-1.313M12 12.75l-2.25-1.313M12 12.75V15m0 6.75l2.25-1.313M12 21.75V19.5m0 2.25l-2.25-1.313m0-16.875L12 2.25l2.25 1.313M21 14.25v2.25l-2.25 1.313m-13.5 0L3 16.5v-2.25"
                    />
                  </svg>
                </div>
                <TopologyCanvas
                  nodes={nodes}
                  edges={edges}
                  nodeTypes={reactFlowNodeTypes}
                  reactFlowWrapper={reactFlowWrapper}
                  reactFlowInstance={reactFlowInstance}
                  editingNodeId={editingNodeId}
                  editingLabel={editingLabel}
                  onNodesChange={onNodesChange}
                  onEdgesChange={onEdgesChange}
                  onConnect={onConnect}
                  onDrop={onDrop}
                  onDragOver={onDragOver}
                  onNodeContextMenu={onNodeContextMenu}
                  onNodeDoubleClick={handleNodeDoubleClick}
                  onEdgeClick={handleEdgeClick}
                  onLabelChange={setEditingLabel}
                  onSaveRename={handleSaveRename}
                  onCancelRename={handleCancelRename}
                  onAutoLayout={handleAutoLayout}
                  loading={
                    sessionLoading ||
                    (aiLoading && !sessionLoading && chatMode === "agent") ||
                    (planActionState.loading &&
                      planActionState.option === "approve")
                  }
                />
              </section>

              {/* Right Sidebar - Device Library (DevicesPaletteContent component) */}
              <aside
                data-tour="device-palette"
                className="col-span-12 lg:col-span-3 flex flex-col border border-(--color-border) rounded-2xl overflow-hidden shadow-sm min-h-[600px] max-h-[80vh] lg:max-h-none"
                dir={isRtl ? "rtl" : "ltr"}
              >
                <DevicesPaletteContent
                  variant="full"
                  nodeTypes={availableNodeTypes}
                  categories={deviceCategories}
                  onDragStart={onDragStart}
                  title={t("topology.device_library.title")}
                  loading={deviceTypesLoading}
                />
              </aside>
            </div>
          </main>

          <NodeContextMenu
            contextMenu={contextMenu}
            onRename={handleRenameNode}
            onDelete={handleDeleteNode}
            onEditProperties={handleEditPropertiesFromContext}
          />

          <ConfirmModal
            show={deleteConfirmState.show}
            title="Delete Chat"
            message="Are you sure you want to delete this chat session? This action cannot be undone."
            confirmText="Delete"
            cancelText="Cancel"
            onConfirm={handleDeleteConfirm}
            onCancel={handleDeleteConfirmClose}
            loading={deleteConfirmState.loading}
            danger={true}
          />

          <ConfirmModal
            show={defaultSwitchConfirmState.show}
            title={t("Switch Topology", { defaultValue: "Switch Topology" })}
            message={t(
              "You already have a topology on the canvas. Switching to this default topology will replace your current canvas.",
              {
                defaultValue:
                  "You already have a topology on the canvas. Switching to this default topology will replace your current canvas.",
              },
            )}
            confirmText={t("Switch", { defaultValue: "Switch" })}
            cancelText={t("Cancel", { defaultValue: "Cancel" })}
            onConfirm={handleDefaultSwitchConfirm}
            onCancel={handleDefaultSwitchConfirmClose}
            loading={false}
            danger={false}
          />

          {/* Revise feedback modal (generic TextInputModal) */}
          <TextInputModal
            show={reviseModalOpen}
            onClose={handleReviseModalClose}
            title={t("Revise plan", { defaultValue: "Revise plan" })}
            description={t(
              "Provide feedback for the plan. Your feedback will be sent to the assistant.",
              {
                defaultValue:
                  "Provide feedback for the plan. Your feedback will be sent to the assistant.",
              },
            )}
            placeholder={t(
              "e.g. Add a firewall between the router and the switch",
              {
                defaultValue:
                  "e.g. Add a firewall between the router and the switch",
              },
            )}
            value={reviseFeedback}
            onChange={setReviseFeedback}
            positiveText={
              aiLoading ? t("generic.submitting") : t("generic.submit_feedback")
            }
            negativeText={t("generic.cancel")}
            onConfirm={handleReviseSubmit}
            loading={aiLoading}
          />

          {/* Submit topology: name this topology before submitting */}
          <TextInputModal
            show={submitNameModalOpen}
            onClose={handleSubmitNameModalClose}
            title={t("topology.submit_topology.name_this_topology")}
            description={t("topology.submit_topology.give_topology_name")}
            placeholder={t("topology.submit_topology.e_g_production_web_tier")}
            value={submitTopologyName}
            onChange={setSubmitTopologyName}
            positiveText={
              submitState.loading
                ? t("generic.submitting")
                : t("generic.submit")
            }
            negativeText={t("generic.cancel")}
            onConfirm={handleSubmitTopology}
            loading={submitState.loading}
            positiveDisabled={!submitTopologyName.trim()}
            actionBadge={
              submitViolations.length > 0 ? (
                <div className="relative group">
                  <span
                    className="inline-flex h-10 items-center gap-2 rounded-md border border-red-500/50 bg-red-500/10 px-3 text-xs font-semibold text-red-300 cursor-pointer"
                    tabIndex={0}
                    role="button"
                    aria-describedby="submit-violations-popover"
                    onClick={() => setSubmitViolationsPopoverDismissed(false)}
                  >
                    <span className="h-2 w-2 rounded-full bg-red-400" />
                    {t("Blocked", { defaultValue: "Blocked" })} (
                    {submitViolations.length}{" "}
                    {submitViolations.length === 1
                      ? t("issue", { defaultValue: "issue" })
                      : t("issues", { defaultValue: "issues" })}
                    )
                  </span>
                  <div
                    id="submit-violations-popover"
                    role="tooltip"
                    className={`absolute bottom-full left-0 mb-2 w-80 rounded-lg border border-red-500/40 bg-(--color-bg) p-3 text-xs text-(--color-text-white) shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-all duration-150 z-50 ${
                      submitViolationsPopoverDismissed
                        ? "opacity-0 pointer-events-none"
                        : "opacity-100 pointer-events-auto group-hover:opacity-100 group-hover:pointer-events-auto"
                    }`}
                  >
                    <button
                      type="button"
                      className="absolute right-2 top-2 text-lg font-semibold text-red-300 transition-colors hover:text-red-200"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSubmitViolationsPopoverDismissed(true);
                      }}
                      aria-label={t("Close", { defaultValue: "Close" })}
                    >
                      ×
                    </button>
                    <p className="text-xs font-semibold text-red-400 mb-2">
                      {t("Save blocked", { defaultValue: "Save blocked" })} ·{" "}
                      {submitViolations.length}{" "}
                      {submitViolations.length === 1
                        ? t("issue", { defaultValue: "issue" })
                        : t("issues", { defaultValue: "issues" })}
                    </p>
                    <ul className="space-y-2 max-h-40 overflow-y-auto pr-1">
                      {submitViolations.map((v, idx) => (
                        <li
                          key={`${v.node_id}-${v.field_path}-${idx}`}
                          className="border-l-2 border-red-500/60 pl-2"
                        >
                          <div className="font-mono text-red-300">
                            {v.node_id} · {v.field_path}
                          </div>
                          <div className="text-(--color-text-muted) mt-0.5">
                            {v.reason}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-(--color-text-muted)">
                      {t(
                        "Close this dialog, open the node properties, remove the flagged content, then save again.",
                      )}
                    </p>
                  </div>
                </div>
              ) : null
            }
          />

          <TopologyPreviewModal
            show={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
            title={previewModalTitle}
            info={previewModalInfo}
            nodes={previewModalNodes}
            edges={previewModalEdges}
          />

          <PropertiesModal
            show={nodePropertiesState.show}
            title={t("Node Properties")}
            subtitle={
              nodePropertiesState.node?.data?.label ||
              nodePropertiesState.node?.id
            }
            data={nodePropertiesState.node?.data}
            excludeKeys={["label", "nodeType"]}
            preserveKeys={{
              label:
                nodePropertiesState.node?.data?.label ||
                nodePropertiesState.node?.id,
              nodeType: nodePropertiesState.node?.data?.nodeType || "device",
            }}
            onClose={handleCloseNodeProperties}
            onSave={handleSaveNodeProperties}
          />

          <PropertiesModal
            show={edgePropertiesState.show}
            title={t("Connection Properties")}
            subtitle={
              edgePropertiesState.edge
                ? `${edgePropertiesState.edge.source} → ${edgePropertiesState.edge.target}`
                : ""
            }
            data={edgePropertiesState.edge?.data}
            onClose={handleCloseEdgeProperties}
            onSave={handleSaveEdgeProperties}
          />

          <JsonPreviewModal
            show={jsonPreviewOpen}
            nodes={nodes}
            edges={edges}
            topologyMeta={currentTopology}
            onClose={handleCloseJsonPreview}
          />

          <PlanViewModal
            show={planViewOpen}
            planMd={planViewMd}
            planStatus={planViewStatus}
            loading={planViewLoading}
            onClose={() => setPlanViewOpen(false)}
            versions={planVersions}
            currentVersion={planCurrentVersion}
            totalVersions={planTotalVersions}
            onVersionChange={handlePlanVersionChange}
            versionLoading={planVersionLoading}
            onPlanAction={(action) => {
              if (isRevertPreview) {
                handleRevertUndo();
              }
              handlePlanOptionSelect(action);
            }}
            planActionLoading={planActionState.loading}
            currentPlanStatus={
              chatHistory.length > 0
                ? [...chatHistory].reverse().find((m) => m.planStatus)
                    ?.planStatus || null
                : null
            }
          />

          <ImportOptionsModal
            show={importModalOpen}
            onClose={handleImportModalClose}
            onImportJson={handleImportJsonFromModal}
            onImportImage={handleImportImageFromModal}
            jsonUploading={jsonUploading}
            imageUploading={imageUploading}
          />

          <ChatHistorySidebar
            open={chatHistorySidebarOpen}
            onClose={() => setChatHistorySidebarOpen(false)}
            sessions={aiSessions}
            selectedSessionId={selectedSessionId}
            onSessionSelect={(id) => {
              handleSelectSession(id);
              setChatHistorySidebarOpen(false);
            }}
            onSessionDelete={handleDeleteSession}
            t={t}
          />
        </div>
      </DeviceTypesContext.Provider>
    </PageLayout>
  );
}

export default Topology;
